import { useChatGptFilter } from "../lib/useChatGptFilter";
import { ChatGptFilterButton } from "../components/ChatGptFilterButton";
import { MockupChooserModal } from "../components/mockup/MockupChooserModal";
import { buildBasePrompt } from "../lib/designPrompt";
import { useDesignModel } from "../lib/useDesignModel";
import { authHeaders } from "../lib/http";
import { useShopVertical } from "../lib/useShopVertical";
import { EtsyListingPreviewBox } from "../components/listing/EtsyListingPreview";
import { useActiveShopId } from "../store/shop.store";
import { PublishToShopsModal } from "../components/publish/PublishToShopsModal";
import { useState, useRef, useEffect, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { comboPrice, checkListingReadiness, listingComboCap, placeChartImages, type ReadinessSection } from "@erc/shared";
import { ReadinessPanel } from "../components/listing/ReadinessPanel";
import { useShotProducts } from "../lib/useShotProducts";
import {
  ShoppingBag,
  Image as ImageIcon,
  Plus,
  Trash2,
  Tag,
  DollarSign,
  Copy,
  ArrowLeft,
  Save,
  X,
  Loader2,
  FileText,
  Calendar,
  Store,
  Package,
  Truck,
  Type,
  AlignLeft,
  Settings2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Upload,
  Layers,
  Video,
  Pencil,
  Settings,
  RefreshCw,
  Send,
  Sparkles,
  Wand2,
  Bookmark,
} from "lucide-react";
import { apiClient } from "../lib/api";
import type { ListingDraft, PersonalizationQuestion } from "@erc/shared";
import { normalizePersonalization, parseEtsyPersonalizationQuestions } from "@erc/shared";
import { PersonalizationEditor } from "../components/listing/PersonalizationEditor";
import { DraftListView } from "../components/listing/DraftListView";
import { etsyProfit } from "../lib/jewelryListing";
import { EtsyPropertiesEditor } from "../components/listing/EtsyPropertiesEditor";
import {
  useTaxonomyProps, baseProps, mergeProps, aiProps, effectiveTemplateDefaults, draftPropsOf, draftStylesOf,
  type ListingProp,
} from "../lib/etsyProperties";
import { findProduct } from "../lib/priceRecalc";
import {
  API_BASE, getStudioExports, clearStudioExports, scoreColor,
  type DraftListItem, type StudioExportData,
} from "../lib/listingDrafts";


// ======== Types ========

interface VariationOption {
  label: string;
  priceAdjust?: number;
  sellingPrice?: number;
  cost?: number;
  sku?: string;
}

interface Variation {
  name: string;
  options: VariationOption[];
}

interface TemplateAttributes {
  /** Renk tablolarina tasarim basilsin mi (liste bazinda; varsayilan otomatik) */
  chartDesign?: "auto" | "on" | "off";
  primaryColor?: string;
  secondaryColor?: string;
  occasion?: string;
  holiday?: string;
  clothingSize?: string;
  sleeveLength?: string;
  neckline?: string;
  clothingStyle?: string;
  condition?: string;
  clothingGraphic?: string;
  sustainability?: string;
  model?: string;
  imageLabels?: string[];
  [key: string]: string | string[] | undefined;
}

interface ShippingProfile {
  shipping_profile_id: number;
  title: string;
  min_processing_days: number;
  max_processing_days: number;
}

interface ProductionPartner {
  production_partner_id: number;
  partner_name: string;
  location: string;
}

interface ShopSection {
  shop_section_id: number;
  title: string;
  rank: number;
  active_listing_count: number;
}

interface ListingTemplateInfo {
  id: string;
  name: string;
  description: string;
  tags: string[];
  category: string | null;
  price: number | null;
  sku: string | null;
  whoMade: string;
  whatIsIt: string;
  whenMade: string;
  isSupply: boolean;
  isDigital: boolean;
  autoRenew: boolean;
  isFeatured: boolean;
  shippingProfileId: string | null;
  taxonomyId: number | null;
  quantity: number;
  listingType: string;
  variations: Variation[] | null;
  disabledCombos?: Record<string, string>[] | null;
  personalization: { enabled: boolean; questions?: PersonalizationQuestion[]; instructions?: string; maxLength?: number } | null;
  imageUrls: string[];
  videoUrl: string | null;
  materials: string[];
  attributes: TemplateAttributes | null;
  sectionId: string | null;
  processingProfileId: string | null;
  productionPartnerId: string | null;
  returnPolicyId: string | null;
}

interface VarTemplate {
  id: string;
  name: string;
  category: string | null;
  variations: Variation[];
  disabledCombos?: Record<string, string>[] | null;
}

interface FormData {
  title: string;
  description: string;
  tags: string[];
  category: string;
  price: string;
  sku: string;
  quantity: string;
  whoMade: string;
  whatIsIt: string;
  whenMade: string;
  isSupply: boolean;
  isDigital: boolean;
  autoRenew: boolean;
  isFeatured: boolean;
  listingType: string;
  shippingProfileId: string;
  taxonomyId: string;
  sectionId: string;
  processingProfileId: string;
  productionPartnerId: string;
  returnPolicyId: string;
  imageUrls: string[];
  imageLabels: string[];
  videoUrl: string;
  variations: Variation[];
  disabledCombos: Record<string, string>[]; // Size×Color cells to turn off (is_enabled=false)
  materials: string[];
  attributes: TemplateAttributes;
  personalization: { enabled: boolean; questions: PersonalizationQuestion[] };
  scheduledAt: string;
}

const EMPTY_FORM: FormData = {
  title: "", description: "", tags: [], category: "", price: "", sku: "",
  quantity: "999", whoMade: "i_did", whatIsIt: "finished_product", whenMade: "made_to_order",
  isSupply: false, isDigital: false, autoRenew: true, isFeatured: false,
  listingType: "physical", shippingProfileId: "", taxonomyId: "", sectionId: "",
  processingProfileId: "", productionPartnerId: "", returnPolicyId: "",
  imageUrls: [], imageLabels: [], videoUrl: "", variations: [], disabledCombos: [], materials: [],
  attributes: {}, personalization: { enabled: false, questions: [] },
  scheduledAt: "",
};

// ======== Helpers ========

function comboSellingPrice(
  opts: Array<{ priceAdjust?: number; sellingPrice?: number }>,
  base: number,
): number {
  return comboPrice(base, opts);
}

// Custom (kisisellestirilebilir) urunse Kisisellestirme'yi otomatik aktif et
// ve soru yoksa bos bir metin sorusu ekle (kullanici doldurur).
function withCustomPersonalization(
  tags: string[],
  pers: { enabled: boolean; questions: PersonalizationQuestion[] },
): { enabled: boolean; questions: PersonalizationQuestion[] } {
  const custom = (tags ?? []).some((t) => t.toLowerCase() === "custom");
  const enabled = custom || pers.enabled;
  const questions = enabled && pers.questions.length === 0
    ? [{ question_type: "text_input" as const, question_text: "", required: true, max_allowed_characters: 50 }]
    : pers.questions;
  return { enabled, questions };
}

// ISO tarihi datetime-local input formatina (YEREL saat, "YYYY-MM-DDTHH:mm") cevir
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

// Verilen aday SKU mevcut SKU'lardan biriyse benzersiz olana kadar arttir.
// "D-L5E7-01" -> "D-L5E7-02" (sondaki sayiyi, basamak sayisini koruyarak arttirir);
// sonda sayi yoksa "-2", "-3"... ekler. Bos aday bos doner.
// suffix: tasarim SKU'su — sayi artirilmaz (D-X-04 baska tasarim sanilir), "-2" eklenir.
function nextFreeSku(candidate: string, existing: Set<string>, suffix = false): string {
  const c = (candidate || "").trim();
  if (!c) return "";
  const has = (s: string) => existing.has(s.toLowerCase());
  if (!has(c)) return c;
  const m = suffix ? null : c.match(/^(.*?)(\d+)$/);
  if (m) {
    const prefix = m[1];
    const width = m[2].length;
    let n = parseInt(m[2], 10);
    let next = "";
    do {
      n += 1;
      next = prefix + String(n).padStart(width, "0");
    } while (has(next));
    return next;
  }
  let i = 2;
  while (has(`${c}-${i}`)) i += 1;
  return `${c}-${i}`;
}

// Liste gorunumu icin hafif tip (API ?light=1 dondurur)
// Etsy'de ayni urunden birden fazla aktif liste (sync-state raporu)
function calcListingScore(f: FormData): number {
  let score = 0;
  if (f.title.length >= 40) score += 15; else if (f.title.length >= 20) score += 8;
  if (f.tags.length >= 13) score += 15; else score += Math.round((f.tags.length / 13) * 15);
  if (f.description.length >= 300) score += 15; else if (f.description.length >= 100) score += 8;
  if (f.imageUrls.length >= 5) score += 15; else score += Math.round((f.imageUrls.length / 5) * 15);
  if (f.variations.length > 0) score += 10;
  if (f.shippingProfileId) score += 10;
  if (f.price && Number(f.price) > 0) score += 10;
  if (Number(f.quantity) > 0) score += 5;
  if (f.whoMade && f.whenMade) score += 5;
  return Math.min(100, score);
}

const getAuthHeaders = authHeaders;

async function fetchShippingProfiles(): Promise<ShippingProfile[]> {
  const res = await fetch(`${API_BASE}/api/etsy/listings/shipping-profiles/list`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return [];
  const json = await res.json();
  return json.data?.results ?? [];
}

async function fetchProductionPartners(): Promise<ProductionPartner[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/production-partners/list`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data?.results ?? json.results ?? [];
  } catch { return []; }
}

interface ReturnPolicy {
  return_policy_id: number;
  accepts_returns: boolean;
  accepts_exchanges: boolean;
}

async function fetchReturnPolicies(): Promise<ReturnPolicy[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/return-policies/list`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data?.results ?? json.results ?? [];
  } catch { return []; }
}

async function fetchShopSections(): Promise<ShopSection[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/shop-sections/list`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data?.results ?? json.results ?? [];
  } catch { return []; }
}

interface ConceptOption {
  id: string;
  name: string;
  emoji: string | null;
  color: string | null;
  keywords: string[];
  defaultSectionId: string | null;
  defaultSectionTitle: string | null;
}

async function fetchConcepts(): Promise<ConceptOption[]> {
  try {
    const res = await fetch(`${API_BASE}/api/concepts?active=true`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data ?? [];
  } catch { return []; }
}

interface ReadinessState {
  readiness_state_id: number;
  readiness_state: string;
  readiness_state_label?: string;
  min_processing_days?: number;
  max_processing_days?: number;
}

async function fetchReadinessStates(): Promise<ReadinessState[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/readiness-states/list`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data?.results ?? json.data?.data?.results ?? json.results ?? [];
  } catch { return []; }
}

interface TaxonomyNode {
  id: number;
  name: string;
  full_path_taxonomy_ids: number[];
  parent_id: number | null;
  children?: TaxonomyNode[];
}

async function fetchTaxonomyNodes(): Promise<TaxonomyNode[]> {
  try {
    const res = await fetch(`${API_BASE}/api/etsy/listings/taxonomy/nodes`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data?.results ?? [];
  } catch { return []; }
}

// Flatten taxonomy tree into searchable list with full path
function flattenTaxonomy(nodes: TaxonomyNode[], parentPath = ""): { id: number; path: string }[] {
  const result: { id: number; path: string }[] = [];
  const seen = new Set<number>();
  function walk(list: TaxonomyNode[], parent: string) {
    for (const node of list) {
      if (seen.has(node.id)) continue;
      seen.add(node.id);
      const path = parent ? `${parent} > ${node.name}` : node.name;
      // Only add leaf nodes (no children) for cleaner selection
      if (!node.children || node.children.length === 0) {
        result.push({ id: node.id, path });
      } else {
        // Also add parent as selectable in case user wants broader category
        result.push({ id: node.id, path });
        walk(node.children, path);
      }
    }
  }
  walk(nodes, parentPath);
  return result;
}

async function fetchListingTemplates(): Promise<ListingTemplateInfo[]> {
  const res = await fetch(`${API_BASE}/api/listing-templates`, { headers: getAuthHeaders() });
  if (!res.ok) return [];
  const json = await res.json();
  return json.data ?? [];
}

async function fetchVarTemplates(): Promise<VarTemplate[]> {
  const res = await fetch(`${API_BASE}/api/variation-templates`, { headers: getAuthHeaders() });
  if (!res.ok) return [];
  const json = await res.json();
  return json.data ?? [];
}

// ======== Section definitions ========

type SectionKey = "images" | "video" | "description" | "tags" | "pricing" | "inventory" | "details" | "variations" | "personalization" | "shipping" | "settings";

const SECTION_MAP: Record<SectionKey, { label: string; icon: typeof Type }> = {
  images: { label: "Görseller", icon: ImageIcon },
  video: { label: "Video", icon: Video },
  description: { label: "Açıklama", icon: AlignLeft },
  tags: { label: "Etiketler", icon: Tag },
  pricing: { label: "Fiyat", icon: DollarSign },
  inventory: { label: "Envanter", icon: Package },
  details: { label: "Detaylar", icon: Settings2 },
  variations: { label: "Varyasyonlar", icon: Layers },
  personalization: { label: "Kişiselleştirme", icon: Pencil },
  shipping: { label: "Gönderim", icon: Truck },
  settings: { label: "Ayarlar", icon: Settings },
};

const DEFAULT_SECTION_ORDER: SectionKey[] = [
  "images", "video", "description", "tags", "pricing", "inventory",
  "details", "variations", "personalization", "shipping", "settings",
];

const SECTION_ORDER_KEY = "erc_listing_section_order";

function loadSectionOrder(): SectionKey[] {
  try {
    const raw = localStorage.getItem(SECTION_ORDER_KEY);
    if (!raw) return DEFAULT_SECTION_ORDER;
    const parsed = JSON.parse(raw) as string[];
    // Validate: must contain all keys
    const valid = parsed.filter((k) => k in SECTION_MAP) as SectionKey[];
    // Add any missing keys at the end
    for (const k of DEFAULT_SECTION_ORDER) {
      if (!valid.includes(k)) valid.push(k);
    }
    return valid;
  } catch { return DEFAULT_SECTION_ORDER; }
}

function saveSectionOrder(order: SectionKey[]) {
  localStorage.setItem(SECTION_ORDER_KEY, JSON.stringify(order));
}

function sectionCount(form: FormData, key: SectionKey): number {
  switch (key) {
    case "images": return form.imageUrls.length;
    case "video": return form.videoUrl ? 1 : 0;
    case "description": return form.description ? 1 : 0;
    case "tags": return form.tags.length;
    case "pricing": return (form.price ? 1 : 0) + (form.quantity && form.quantity !== "999" ? 1 : 0);
    case "inventory": return (form.whoMade !== "i_did" ? 1 : 0) + (form.whenMade !== "made_to_order" ? 1 : 0);
    case "details": return (form.category ? 1 : 0) + (form.taxonomyId ? 1 : 0) + Object.values(withoutSystemAttrs(form.attributes)).filter((v) => v && v !== "Sec" && typeof v !== "object").length;
    case "variations": return form.variations.length;
    case "personalization": return form.personalization.enabled ? 1 : 0;
    case "shipping": return (form.shippingProfileId ? 1 : 0) + (form.processingProfileId ? 1 : 0);
    case "settings": return (form.sectionId ? 1 : 0) + (form.isFeatured ? 1 : 0) + (form.returnPolicyId ? 1 : 0);
  }
}

// ======== Attribute options ========

/** Varyasyon adi onerileri — serbest yazilabilir (takida "Number of Hearts" vb.) */
const VARIATION_NAME_SUGGESTIONS = [
  "Size", "Color", "Style", "Material", "Length", "Width", "Finish", "Chain Length",
  "Number of Hearts", "Number of Stones", "Birthstone", "Ring Size", "Scent", "Pattern", "Device",
];

/** Taslak attributes icindeki SISTEM alanlari (taki kaynagi, fiyatlama) —
 *  sayilmaz, sablona kaydedilmez, sablon uygulanirken korunur */
// comboPrices: taslaga ozel kombinasyon fiyati (en ucuz varyasyon) — sablona gecerse
// baska urunlere yanlis fiyat tasir
// etsyProperties/styles: taslaga ozel (sablonun varsayilanlari etsyPropertyDefaults'ta)
const SYSTEM_ATTR_KEYS = ["supplier", "pricing", "marketplace", "trendyol", "comboPrices", "etsyProperties", "styles"];
const withoutSystemAttrs = (a: Record<string, unknown> | null | undefined) =>
  Object.fromEntries(Object.entries(a ?? {}).filter(([k]) => !SYSTEM_ATTR_KEYS.includes(k)));


function ListingForm({ draftId, etsyId }: { draftId: string | null; etsyId?: string | null }) {
  const navigate = useNavigate();
  const activeShop = useActiveShopId();
  const [form, setForm] = useState<FormData>({ ...EMPTY_FORM });
  const [sectionOrder, setSectionOrder] = useState<SectionKey[]>(loadSectionOrder);
  const [activeSection, setActiveSection] = useState<SectionKey>(() => sectionOrder[0] || "images");
  const [loading, setLoading] = useState(!!draftId || !!etsyId);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [resyncingImgs, setResyncingImgs] = useState(false);
  // Mockuplar yenilendi/tasarim degisti: yeni gorseller taslakta, Etsy'ye onayla gider
  const [etsyImagesPending, setEtsyImagesPending] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(draftId);
  const [currentEtsyListingId, setCurrentEtsyListingId] = useState<string | null>(null);
  // Etsy'de liste yayinda mi (active) yoksa taslak mi — buton/rozet icin
  const [listingActive, setListingActive] = useState(false);
  // Liste planlandi mi (status=scheduled) — planlanan tarihi goster/duzenle
  const [isScheduled, setIsScheduled] = useState(false);
  // Fullscreen image lightbox (index into form.imageUrls) + drag-reorder state.
  const [imgLightbox, setImgLightbox] = useState<number | null>(null);
  const [dragImgIdx, setDragImgIdx] = useState<number | null>(null);
  useEffect(() => {
    if (imgLightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setImgLightbox(null);
      else if (e.key === "ArrowRight") setImgLightbox((i) => (i === null ? i : (i + 1) % form.imageUrls.length));
      else if (e.key === "ArrowLeft") setImgLightbox((i) => (i === null ? i : (i - 1 + form.imageUrls.length) % form.imageUrls.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgLightbox, form.imageUrls.length]);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showPublishShops, setShowPublishShops] = useState(false);
  const [scheduledList, setScheduledList] = useState<Array<{ id: string; title: string; scheduledAt: string | null }>>([]);
  const [originalTitle, setOriginalTitle] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [materialInput, setMaterialInput] = useState("");
  const [newOptionInputs, setNewOptionInputs] = useState<Record<number, string>>({});
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const titleRef = useRef<HTMLDivElement | null>(null);
  const { vertical: formVertical, shop: activeShopInfo } = useShopVertical();

  // ── Kaydedilmemis degisiklik + otomatik kayit ──
  // Var olan taslak degisince 4 sn sonra sessizce kaydedilir; yeni taslak
  // (henuz kayit yok) otomatik OLUSTURULMAZ, sayfadan cikarken uyarilir.
  const [savedSig, setSavedSig] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const formSig = useMemo(() => JSON.stringify(form), [form]);
  const dirty = savedSig !== null && formSig !== savedSig;
  useEffect(() => {
    if (!loading && savedSig === null) setSavedSig(formSig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  useEffect(() => {
    if (!dirty || !currentDraftId || saving || publishing) return;
    const t = setTimeout(() => { void handleSave(undefined, { silent: true }); }, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSig, dirty, currentDraftId, saving, publishing]);
  useEffect(() => {
    if (!dirty) return;
    const onLeave = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  // Kaynak tasarim(lar) — taslakla birlikte gelir (DesignListingLink)
  const [linkedDesigns, setLinkedDesigns] = useState<LinkedDesign[]>([]);
  // Ilham alinan analizler (satan liste + mockup gorseli)
  const [inspirations, setInspirations] = useState<InspirationAnalysis[]>([]);
  // Taki taslagi: tedarikci urunu var ya da taki magazasi
  const supplierAttr = (form.attributes?.supplier ?? null) as JewelrySupplierAttr | null;
  const pricingAttr = (form.attributes?.pricing ?? null) as JewelryPricingAttr | null;
  const comboPricesAttr = (form.attributes?.comboPrices ?? null) as Array<{ match: Record<string, string>; price: number }> | null;
  const isJewelryForm = formVertical === "jewelry" || !!supplierAttr?.categoryId;

  // Etsy kategori ozellikleri (attributes.etsyProperties) ve stiller (attributes.styles)
  const taxPropsQ = useTaxonomyProps(form.taxonomyId ? Number(form.taxonomyId) : null);
  const { data: shotDataForProps } = useShotProducts(isJewelryForm);
  const etsyProps = draftPropsOf(form.attributes) ?? [];
  const etsyStyles = draftStylesOf(form.attributes);
  const [propsFilling, setPropsFilling] = useState(false);
  const [propsError, setPropsError] = useState<string | null>(null);
  // attributes tipi metin alanlari icin; sistem alanlari JSON olarak tasinir
  const setEtsyProps = (next: ListingProp[]) =>
    setForm((prev) => ({ ...prev, attributes: { ...prev.attributes, etsyProperties: next as unknown as string[] } }));
  const setEtsyStyles = (next: string[]) =>
    setForm((prev) => ({ ...prev, attributes: { ...prev.attributes, styles: next } }));
  async function autoFillEtsyProps() {
    const taxProps = taxPropsQ.data;
    if (!taxProps) return;
    setPropsFilling(true);
    setPropsError(null);
    try {
      const product = supplierAttr?.categoryId
        ? findProduct((shotDataForProps?.catalogs ?? []) as Array<{ id: string; categories: unknown[] }>, supplierAttr.catalogId, supplierAttr.categoryId)
        : null;
      const tpl = listingTemplates?.find((t) => t.id === appliedListingTemplateId);
      const base = baseProps(taxProps, {
        product, materials: form.materials,
        hasPersonalization: form.personalization.enabled && form.personalization.questions.length > 0,
        variations: form.variations,
      }, effectiveTemplateDefaults(tpl?.attributes, isJewelryForm));
      // Elle secilenler korunur; sonra kural/sablon; sonra onceki AI secimleri
      const kept = mergeProps(etsyProps.filter((p) => p.source === "manual"), base, etsyProps);
      const src = (product as unknown as { source?: { attributes?: Record<string, string> }; inspirations?: Array<{ title?: string | null; tags?: string[] }> } | null);
      const ai = await aiProps({
        taxProps, filled: kept, productName: product?.name ?? form.title, title: form.title, tags: form.tags,
        description: form.description, attributes: src?.source?.attributes,
        inspirations: src?.inspirations?.map((x) => ({ title: x.title, tags: x.tags })),
      });
      setEtsyProps(mergeProps(kept, ai.props));
      if (!etsyStyles.length && ai.styles.length) setEtsyStyles(ai.styles);
    } catch (e) {
      setPropsError(e instanceof Error ? e.message : String(e));
    } finally {
      setPropsFilling(false);
    }
  }
  // Ortak "Etsy'ye hazir mi" kurallari (sunucu yayini ve Liste Denetimi ile ayni)
  const readiness = useMemo(() => checkListingReadiness({
    title: form.title, description: form.description, price: form.price, quantity: form.quantity,
    tags: form.tags, imageUrls: form.imageUrls, taxonomyId: form.taxonomyId || null,
    shippingProfileId: form.shippingProfileId || null, listingType: form.listingType,
    variations: form.variations, personalization: form.personalization,
  }), [form]);
  const blockedSections = useMemo(() => new Set(readiness.blockers.map((b) => b.section)), [readiness]);
  function goSection(section: ReadinessSection) {
    if (section === "title") { titleRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    setActiveSection(section as SectionKey);
    sectionRefs.current[section]?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  const imageFileRef = useRef<HTMLInputElement>(null);
  const videoFileRef = useRef<HTMLInputElement>(null);

  // AI generation state
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState<Record<string, boolean>>({});
  const [showAiInput, setShowAiInput] = useState<Record<string, boolean>>({});
  const [selectedConceptId, setSelectedConceptId] = useState<string>("");
  const [conceptAutofillSection, setConceptAutofillSection] = useState(true);
  const { data: concepts = [] } = useQuery({ queryKey: ["concepts", "active"], queryFn: fetchConcepts, staleTime: 60_000 });
  const selectedConcept = useMemo(() => concepts.find((c) => c.id === selectedConceptId), [concepts, selectedConceptId]);

  const updateForm = (partial: Partial<FormData>) => setForm((prev) => ({ ...prev, ...partial }));

  function moveSectionOrder(from: number, to: number) {
    if (to < 0 || to >= sectionOrder.length) return;
    const newOrder = [...sectionOrder];
    [newOrder[from], newOrder[to]] = [newOrder[to], newOrder[from]];
    setSectionOrder(newOrder);
    saveSectionOrder(newOrder);
  }

  // Fetch Etsy profiles
  const { data: shippingProfiles, refetch: refetchProfiles } = useQuery({ queryKey: ["etsy-shipping-profiles", activeShop ?? "no-shop"], queryFn: fetchShippingProfiles, staleTime: 5 * 60_000 });
  // Kargo profili bossa: son secilen (hatirlanan), yoksa "USA Cargo"
  useEffect(() => {
    if (form.shippingProfileId || !shippingProfiles?.length) return;
    let last = "";
    try { last = localStorage.getItem("erc:last-shipping-profile") ?? ""; } catch { /* */ }
    const hit = shippingProfiles.find((p) => String(p.shipping_profile_id) === last)
      ?? shippingProfiles.find((p) => /usa\s*cargo/i.test(p.title ?? "")) ?? shippingProfiles.find((p) => /\busa\b/i.test(p.title ?? ""));
    if (hit) updateForm({ shippingProfileId: String(hit.shipping_profile_id) });
  }, [shippingProfiles, form.shippingProfileId]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data: productionPartners, refetch: refetchPartners, isLoading: partnersLoading } = useQuery({ queryKey: ["etsy-production-partners", activeShop ?? "no-shop"], queryFn: fetchProductionPartners, staleTime: 5 * 60_000 });
  const { data: returnPolicies } = useQuery({ queryKey: ["etsy-return-policies", activeShop ?? "no-shop"], queryFn: fetchReturnPolicies, staleTime: 5 * 60_000 });
  const { data: shopSections, refetch: refetchSections } = useQuery({ queryKey: ["etsy-shop-sections", activeShop ?? "no-shop"], queryFn: fetchShopSections, staleTime: 5 * 60_000 });
  const { data: readinessStates, refetch: refetchReadinessStates } = useQuery({ queryKey: ["etsy-readiness-states", activeShop ?? "no-shop"], queryFn: fetchReadinessStates, staleTime: 5 * 60_000 });
  const { data: taxonomyNodes } = useQuery({ queryKey: ["etsy-taxonomy"], queryFn: fetchTaxonomyNodes, staleTime: 30 * 60_000 });
  const { data: listingTemplates } = useQuery({ queryKey: ["listing-templates"], queryFn: fetchListingTemplates, staleTime: 60_000 });
  // Listing template chosen in Mockup Studio, applied once templates load.
  const [pendingListingTemplateId, setPendingListingTemplateId] = useState<string | null>(null);
  // Mockup Studio'dan gelen kaynak tasarim + kullanilan mockup sablonlari + uygulanan liste sablonu
  // (kaydederken listing'e yazilir, olusturunca tasarima baglanir)
  const [sourceDesignId, setSourceDesignId] = useState<string | null>(null);
  const [sourceDesignSku, setSourceDesignSku] = useState<string>("");
  const [sourceMockupTemplates, setSourceMockupTemplates] = useState<string[]>([]);
  // Arka plan yayin kuyrugu durum izleyicisi
  const publishPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [appliedListingTemplateId, setAppliedListingTemplateId] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingListingTemplateId || !listingTemplates) return;
    const tpl = listingTemplates.find((t) => t.id === pendingListingTemplateId);
    if (tpl) { applyListingTemplate(tpl); setAppliedListingTemplateId(tpl.id); setPendingListingTemplateId(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingListingTemplateId, listingTemplates]);

  // SEO title/description/tags generated in Mockup Studio — applied AFTER any
  // listing template (so the design-specific SEO copy wins).
  const [pendingSeo, setPendingSeo] = useState<{ title?: string; description?: string; tags?: string[] } | null>(null);
  useEffect(() => {
    if (!pendingSeo || pendingListingTemplateId) return; // wait until template (if any) applied
    updateForm({
      ...(pendingSeo.title ? { title: pendingSeo.title } : {}),
      ...(pendingSeo.description ? { description: pendingSeo.description } : {}),
      ...(pendingSeo.tags?.length ? { tags: pendingSeo.tags.slice(0, 13) } : {}),
    });
    setPendingSeo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSeo, pendingListingTemplateId]);

  // Auto-apply the last-used listing template on a fresh new listing (not when
  // editing a draft/Etsy listing, and not when Studio already chose one or sent SEO).
  const autoTemplateAppliedRef = useRef(false);
  useEffect(() => {
    if (autoTemplateAppliedRef.current) return;
    if (draftId || etsyId || pendingListingTemplateId || pendingSeo) return;
    if (!listingTemplates || listingTemplates.length === 0) return;
    let last: string | null = null;
    try { last = localStorage.getItem("erc_last_listing_template"); } catch { /* ignore */ }
    const tpl = last ? listingTemplates.find((t) => t.id === last) : null;
    if (!tpl) return;
    autoTemplateAppliedRef.current = true;
    applyListingTemplate(tpl);
    setAppliedListingTemplateId(tpl.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingTemplates, pendingListingTemplateId, pendingSeo, draftId, etsyId]);
  const { data: varTemplates } = useQuery({ queryKey: ["variation-templates"], queryFn: fetchVarTemplates, staleTime: 60_000 });

  // Flatten taxonomy for searchable dropdown
  const flatTaxonomy = useMemo(() => taxonomyNodes ? flattenTaxonomy(taxonomyNodes) : [], [taxonomyNodes]);
  const [taxonomySearch, setTaxonomySearch] = useState("");

  // Mockup'larin birden fazla kez islenmesini onlemek icin ref
  const mockupsProcessedRef = useRef(false);

  // Load draft + merge pending mockups atomically
  useEffect(() => {
    let stale = false;
    mockupsProcessedRef.current = false;
    if (draftId) {
      setLoading(true);
      apiClient.getListingDraft(draftId).then(async (draft) => {
        if (stale) return; // StrictMode'da eski yaniti yoksay

        // Cast to any for extended fields that may exist in DB but not in shared type
        const d = draft as unknown as Record<string, unknown>;
        const vars = (draft.variations as Variation[]) ?? [];
        const pers = normalizePersonalization(draft.personalization as Parameters<typeof normalizePersonalization>[0]);
        const draftImageUrls = draft.imageUrls ?? [];
        const draftImageLabels = draftImageUrls.map(() => "");

        // Bekleyen mockup gorsellerini kontrol et (atomik birlestirme)
        let pendingMockupUrls: string[] = [];
        let studioDesignSku = "";
        if (!mockupsProcessedRef.current) {
          mockupsProcessedRef.current = true;
          const studioData = getStudioExports();
          let pendingItems: { id: string; url: string }[] = [];
          if (studioData.images.length > 0) {
            if (studioData.listingTemplateId) setPendingListingTemplateId(studioData.listingTemplateId);
            if (studioData.designId) setSourceDesignId(studioData.designId);
            if (studioData.mockupTemplates?.length) setSourceMockupTemplates(studioData.mockupTemplates);
            if (studioData.designSku) { studioDesignSku = studioData.designSku; setSourceDesignSku(studioData.designSku); }
            if (studioData.title || studioData.description || studioData.tags) {
              setPendingSeo({ title: studioData.title, description: studioData.description, tags: studioData.tags });
            }
            clearStudioExports();
            pendingItems = studioData.images;
          } else {
            const pendingRaw = sessionStorage.getItem("erc_pending_mockups");
            if (pendingRaw) {
              sessionStorage.removeItem("erc_pending_mockups");
              try { pendingItems = JSON.parse(pendingRaw); } catch { /* ignore */ }
            }
          }
          if (pendingItems.length > 0) {
            // data URL'leri sunucuya yukle, server URL'leri direkt kullan
            const uploadPromises = pendingItems.map((item, i) =>
              item.url.startsWith("data:") ? uploadDataUrl(item.url, i) : Promise.resolve(item.url)
            );
            pendingMockupUrls = (await Promise.all(uploadPromises)).filter(Boolean) as string[];
          }
        }

        if (stale) return; // await'lerden sonra tekrar kontrol

        // Draft gorselleri ile mockup gorsellerini birlestir
        const existingUrls = new Set(draftImageUrls);
        const newMockupUrls = pendingMockupUrls.filter((u) => !existingUrls.has(u));
        const mergedUrls = [...draftImageUrls, ...newMockupUrls].slice(0, 20);
        const mergedLabels = [...draftImageLabels, ...newMockupUrls.map(() => "mockup")].slice(0, 20);
        const sorted = sortImagesChartLast(mergedUrls, mergedLabels);

        setOriginalTitle(draft.title || "");
        setForm({
          ...EMPTY_FORM,
          // Etsy en fazla 13 etiket kabul eder. Fazlasi kaydedilirken 400
          // donuyor; forma girerken kirpiyoruz ki kullanici kilitli kalmasin.
          title: draft.title || "", description: draft.description, tags: (draft.tags ?? []).slice(0, 13),
          price: draft.price ? String(draft.price) : "", quantity: String(draft.quantity ?? 999),
          whoMade: draft.whoMade || "i_did", whatIsIt: (d.whatIsIt as string) || "finished_product",
          whenMade: draft.whenMade || "made_to_order", isSupply: draft.isSupply ?? false,
          isDigital: (d.isDigital as boolean) ?? false, autoRenew: (d.autoRenew as boolean) ?? true,
          isFeatured: (d.isFeatured as boolean) ?? false, listingType: draft.listingType || "physical",
          shippingProfileId: draft.shippingProfileId || "", sectionId: draft.sectionId || "",
          taxonomyId: draft.taxonomyId ? String(draft.taxonomyId) : "",
          processingProfileId: (d.processingProfileId as string) || "made_to_order_1_3",
          productionPartnerId: (d.productionPartnerId as string) || "", returnPolicyId: (d.returnPolicyId as string) || "",
          imageUrls: sorted.imageUrls, imageLabels: sorted.imageLabels,
          videoUrl: (d.videoUrl as string) || "", variations: vars,
          disabledCombos: (d.disabledCombos as Record<string, string>[]) ?? [],
          materials: (d.materials as string[]) ?? [],
          attributes: (d.attributes as TemplateAttributes) || {},
          personalization: withCustomPersonalization(draft.tags ?? [], { enabled: pers.enabled, questions: pers.questions }),
          scheduledAt: draft.scheduledAt ? toDatetimeLocal(draft.scheduledAt) : "",
          sku: studioDesignSku || (d.sku as string) || "", category: (d.category as string) || "",
        });
        setCurrentDraftId(draft.id);
        // Etsy listesi SADECE secili magazaya aitse forma baglanir. Baska bir
        // magazaya (ya da hicbir magazaya) bagli kayit, bu magaza icin YENI
        // liste olarak yayinlanir — aksi halde "Guncelle" baska magazanin
        // listesine yazmaya calisir ve Etsy hata doner.
        const boundHere = draft.etsyListingId && (d.shopId as string | null | undefined) === activeShop;
        if (boundHere) setCurrentEtsyListingId(draft.etsyListingId);
        setListingActive(draft.status === "published" && Boolean(boundHere));
        {
          const ds = ((draft as unknown as { designLinks?: Array<{ design: LinkedDesign }> }).designLinks ?? []).map((l) => l.design).filter(Boolean);
          setLinkedDesigns(ds);
          setInspirations(inspirationsOf(draft, ds));
        }
        setIsScheduled(draft.status === "scheduled");
        // Kayitli liste sablonu varsa dropdown'da secili goster
        if (d.templateId) setAppliedListingTemplateId(d.templateId as string);

        // Yeni mockup eklendiyse sunucuya kaydet
        if (newMockupUrls.length > 0) {
          try {
            await fetch(`${API_BASE}/api/listing-drafts/${draft.id}`, {
              method: "PATCH",
              headers: getAuthHeaders(),
              body: JSON.stringify({ imageUrls: sorted.imageUrls }),
            });
            setSaveMsg(`${newMockupUrls.length} mockup eklendi ve kaydedildi`);
          } catch {
            setSaveMsg(`${newMockupUrls.length} mockup eklendi`);
          }
          setTimeout(() => setSaveMsg(null), 3000);
        }
      }).catch(() => {}).finally(() => { if (!stale) setLoading(false); });
    }
    return () => { stale = true; };
  }, [draftId]);

  // Load an existing Etsy listing directly (Etsy Urunlerim -> Duzenle). Populates
  // the form and sets currentEtsyListingId so saving UPDATES the Etsy listing.
  useEffect(() => {
    if (!etsyId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/etsy/listings/${etsyId}`, { headers: getAuthHeaders() });
        const j = await r.json();
        const d = j.data;
        if (!d || cancelled) return;
        const priceNum = d.price && typeof d.price === "object" && d.price.divisor
          ? d.price.amount / d.price.divisor
          : Number(d.price) || 0;
        const imgs: string[] = Array.isArray(d.images) ? d.images.map((im: { url_fullxfull?: string }) => im.url_fullxfull).filter(Boolean) : [];
        const pers = normalizePersonalization(d.personalization ?? null);
        setOriginalTitle(d.title || "");
        setCurrentEtsyListingId(String(etsyId));
        setForm((prev) => ({
          ...prev,
          title: d.title || "",
          description: d.description || "",
          tags: Array.isArray(d.tags) ? d.tags.slice(0, 13) : [],
          price: priceNum ? String(priceNum) : "",
          quantity: String(d.quantity ?? 999),
          taxonomyId: d.taxonomy_id ? String(d.taxonomy_id) : "",
          whoMade: d.who_made || "i_did",
          whenMade: d.when_made || "made_to_order",
          isSupply: !!d.is_supply,
          listingType: d.listing_type || "physical",
          shippingProfileId: d.shipping_profile_id ? String(d.shipping_profile_id) : "",
          sectionId: d.shop_section_id ? String(d.shop_section_id) : "",
          sku: Array.isArray(d.skus) && d.skus[0] ? String(d.skus[0]) : prev.sku,
          materials: Array.isArray(d.materials) ? d.materials : [],
          imageUrls: imgs,
          imageLabels: imgs.map(() => ""),
          personalization: { enabled: pers.enabled, questions: pers.questions },
        }));
      } catch {
        setSaveMsg("Etsy listesi yüklenemedi");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etsyId]);

  // Upload a data URL as a file to the server, returns server URL
  async function uploadDataUrl(dataUrl: string, index: number): Promise<string | null> {
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      // Dosya tipini/uzantisini gercek icerikten al (JPEG mockup PNG olarak damgalanmasin)
      const mime = blob.type || (dataUrl.match(/^data:([^;]+)/)?.[1]) || "image/png";
      const ext = mime === "image/jpeg" ? "jpg" : mime === "image/webp" ? "webp" : "png";
      const file = new File([blob], `mockup_${Date.now()}_${index}.${ext}`, { type: mime });
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await fetch(`${API_BASE}/api/assets/upload-placeholder`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!uploadRes.ok) return null;
      const json = await uploadRes.json();
      return json.data?.url || null;
    } catch { return null; }
  }

  // Mockup gorsellerini yukle, forma ekle ve otomatik kaydet
  async function processMockupImages(items: { id: string; url: string }[]) {
    if (items.length === 0) return;

    const total = items.length;
    setSaveMsg(`Mockup görselleri yükleniyor... (0/${total})`);

    // Mockup sirasini koru + sablonun ekledigi diger gorselleri (size chart vb.) EZME.
    // Her tamamlanan mockup, o anki TAZE prev'e (guncel sablon gorselleriyle) yeniden
    // harmanlanir; mockup-disi gorseller ("mockup" olmayan label) oldugu gibi korunur.
    const results: (string | null)[] = new Array(total).fill(null);
    let newImageUrls: string[] = [];
    const applyProgress = () =>
      setForm((prev) => {
        // Mockup olmayan gorselleri (sablon chart/detay, kullanici yuklemesi) koru
        const keepUrls: string[] = [];
        const keepLabels: string[] = [];
        prev.imageUrls.forEach((u, i) => {
          if ((prev.imageLabels[i] || "") !== "mockup") { keepUrls.push(u); keepLabels.push(prev.imageLabels[i] || ""); }
        });
        const keepSet = new Set(keepUrls);
        // Mockuplari orijinal (studio) sirasinda ekle
        const orderedMocks = (results.filter(Boolean) as string[]).filter((u) => !keepSet.has(u));
        const mergedUrls = [...orderedMocks, ...keepUrls].slice(0, 20);
        const mergedLabels = [...orderedMocks.map(() => "mockup"), ...keepLabels].slice(0, 20);
        const sorted = sortImagesChartLast(mergedUrls, mergedLabels);
        newImageUrls = sorted.imageUrls;
        return { ...prev, ...sorted };
      });

    let done = 0;
    await Promise.all(
      items.map(async (item, i) => {
        const url = item.url.startsWith("data:") ? await uploadDataUrl(item.url, i) : item.url;
        results[i] = url;
        done++;
        if (url) applyProgress();
        setSaveMsg(`Mockup görselleri yükleniyor... (${done}/${total})`);
      })
    );

    if (newImageUrls.length === 0) {
      setSaveMsg("Görsel yükleme başarısız");
      setTimeout(() => setSaveMsg(null), 3000);
      return;
    }
    const uploadedUrls = results.filter(Boolean) as string[];

    // Sunucuya otomatik kaydet
    if (currentDraftId && newImageUrls.length > 0) {
      try {
        await fetch(`${API_BASE}/api/listing-drafts/${currentDraftId}`, {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify({ imageUrls: newImageUrls }),
        });
        setSaveMsg(`${uploadedUrls.length} mockup eklendi ve kaydedildi`);
      } catch {
        setSaveMsg(`${uploadedUrls.length} mockup eklendi (kaydetmek için Kaydet'e basın)`);
      }
    } else {
      setSaveMsg(`${uploadedUrls.length} mockup eklendi`);
    }
    setTimeout(() => setSaveMsg(null), 3000);
  }

  // Yeni listing icin (draftId yoksa) bekleyen mockup'lari isle
  const newListingProcessedRef = useRef(false);
  useEffect(() => {
    if (loading || draftId || newListingProcessedRef.current) return;
    newListingProcessedRef.current = true;

    // Read from localStorage first (direct from MockupStudio)
    const studioData = getStudioExports();
    if (studioData.images.length > 0) {
      clearStudioExports();
      processMockupImages(studioData.images);
      if (studioData.videoUrl) {
        updateForm({ videoUrl: studioData.videoUrl });
      }
      if (studioData.listingTemplateId) setPendingListingTemplateId(studioData.listingTemplateId);
      if (studioData.designId) setSourceDesignId(studioData.designId);
      if (studioData.mockupTemplates?.length) setSourceMockupTemplates(studioData.mockupTemplates);
      if (studioData.designSku) { setSourceDesignSku(studioData.designSku); updateForm({ sku: studioData.designSku }); }
      if (studioData.title || studioData.description || studioData.tags) {
        setPendingSeo({ title: studioData.title, description: studioData.description, tags: studioData.tags });
      }
      // Auto-populate AI prompt with design analysis keywords
      if (studioData.designAnalysis) {
        const da = studioData.designAnalysis;
        const autoKeyword = da.keywords.slice(0, 3).join(", ");
        if (autoKeyword) setAiPrompt(autoKeyword);
        // Store analysis for AI generation context
        (window as unknown as Record<string, unknown>).__designAnalysisContext = da;
      }
      return;
    }

    // Fallback: read from sessionStorage (redirected via DraftListView)
    const pending = sessionStorage.getItem("erc_pending_mockups");
    if (pending) {
      sessionStorage.removeItem("erc_pending_mockups");
      try {
        const items = JSON.parse(pending) as { id: string; url: string }[];
        if (items.length > 0) processMockupImages(items);
      } catch { /* ignore */ }
    }
  }, [loading, draftId]);

  // IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { for (const entry of entries) { if (entry.isIntersecting) { const key = entry.target.getAttribute("data-section"); if (key) setActiveSection(key as SectionKey); } } },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );
    for (const key of Object.keys(sectionRefs.current)) {
      const el = sectionRefs.current[key];
      if (el) { el.setAttribute("data-section", key); observer.observe(el); }
    }
    return () => observer.disconnect();
  }, [loading]);

  const score = useMemo(() => calcListingScore(form), [form]);

  // ---- Save draft ----
  /** Kullanici videoyu "Kaldir" ile sildiyse kayitta null gonderilir */
  const videoRemovedRef = useRef(false);
  /** Arka planda uretilen videoyu bekler; hazir olunca (form hala bossa) forma yazar */
  const videoWatchRef = useRef<string | null>(null);
  function watchDraftVideo(draftId: string) {
    if (videoWatchRef.current === draftId) return;
    videoWatchRef.current = draftId;
    let tries = 0;
    const tick = async () => {
      if (videoWatchRef.current !== draftId || tries++ > 30) { videoWatchRef.current = null; return; }
      try {
        const d = await apiClient.getListingDraft(draftId) as { videoUrl?: string | null };
        if (d?.videoUrl) {
          setForm((prev) => (prev.videoUrl ? prev : { ...prev, videoUrl: d.videoUrl! }));
          setSaveMsg("Video hazır — Etsy'ye gönderince yüklenir");
          videoWatchRef.current = null;
          return;
        }
      } catch { /* sonraki denemede */ }
      setTimeout(() => void tick(), 5000);
    };
    setSaveMsg("Video oluşturuluyor… (arka planda, yaklaşık 1 dakika)");
    setTimeout(() => void tick(), 4000);
  }

  async function handleSave(status?: string, opts?: { silent?: boolean }): Promise<string | null> {
    const silent = !!opts?.silent;
    if (!form.title.trim()) {
      if (!silent) { setSaveMsg("Başlık gerekli"); setTimeout(() => setSaveMsg(null), 3000); }
      return null;
    }
    const savingSig = JSON.stringify(form);
    setSaving(true);
    let savedId: string | null = currentDraftId;
    // Only send fields the API schema accepts, skip empty optional strings (zod min(1) rejects "")
    const apiPayload: Record<string, unknown> = {
      title: form.title.trim(),
      tags: form.tags.slice(0, 13),
      quantity: Number(form.quantity) || 1,
      whoMade: form.whoMade,
      whenMade: form.whenMade,
      isSupply: form.isSupply,
      listingType: form.listingType,
      imageUrls: form.imageUrls,
      // Bos video alani videoyu SILMEZ (arka planda uretilmis olabilir); yalnizca
      // "Kaldir" ile silinir
      ...(form.videoUrl ? { videoUrl: form.videoUrl } : videoRemovedRef.current ? { videoUrl: null } : {}),
      // Video yoksa kaydetmek urun fotograflarindan video uretimini baslatir (sunucuda)
      ...(!form.videoUrl && !videoRemovedRef.current && form.imageUrls.length > 0 ? { video: { aspectRatio: "1:1", duration: 8 } } : {}),
    };
    // For create, description is required; for update, only send if non-empty
    if (form.description.trim()) {
      apiPayload.description = form.description.trim();
    } else if (!currentDraftId) {
      apiPayload.description = " ";
    }
    // Guncellemede bosaltilan alanlar da gonderilir (null / bos dizi) — yoksa
    // kaldirilan varyasyon, kisisellestirme, SKU vb. eski haliyle geri geliyordu
    const clear = <T,>(v: T | null | undefined | "" | false, empty: T | null = null) =>
      (v !== undefined && v !== null && v !== "" && v !== false ? v : currentDraftId ? empty : undefined);
    if (form.price) apiPayload.price = Number(form.price);
    apiPayload.taxonomyId = clear(form.taxonomyId ? Number(form.taxonomyId) : null);
    apiPayload.shippingProfileId = clear(form.shippingProfileId);
    apiPayload.sectionId = clear(form.sectionId);
    apiPayload.variations = form.variations.length > 0 ? form.variations : clear(null, []);
    apiPayload.disabledCombos = form.disabledCombos.length > 0 ? form.disabledCombos : clear(null, []);
    apiPayload.personalization = form.personalization.enabled
      ? { enabled: true, questions: form.personalization.questions }
      : clear(null);
    // datetime-local ("2026-07-21T14:30") -> tam ISO (zod .datetime() bunu ister)
    if (form.scheduledAt) apiPayload.scheduledAt = new Date(form.scheduledAt).toISOString();
    if (status) apiPayload.status = status;
    // Kaynak tasarim akisi: kullanilan liste sablonu + mockup sablon adlarini kaydet
    if (appliedListingTemplateId) apiPayload.templateId = appliedListingTemplateId;
    if (sourceMockupTemplates.length > 0) apiPayload.mockupTemplates = sourceMockupTemplates;
    // Onceden DB'ye kaydedilmeyen alanlar (kaydet/publish sonrasi kaybolmasin)
    apiPayload.whatIsIt = form.whatIsIt;
    apiPayload.isDigital = form.isDigital;
    apiPayload.autoRenew = form.autoRenew;
    apiPayload.isFeatured = form.isFeatured;
    apiPayload.sku = clear(form.sku);
    apiPayload.category = clear(form.category);
    apiPayload.materials = form.materials.length > 0 ? form.materials : clear(null, []);
    if (form.attributes && Object.keys(form.attributes).length > 0) apiPayload.attributes = form.attributes;
    apiPayload.returnPolicyId = clear(form.returnPolicyId);
    apiPayload.processingProfileId = clear(form.processingProfileId);
    apiPayload.productionPartnerId = clear(form.productionPartnerId);
    try {
      const url = currentDraftId
        ? `${API_BASE}/api/listing-drafts/${currentDraftId}`
        : `${API_BASE}/api/listing-drafts`;
      const method = currentDraftId ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(apiPayload),
      });
      const json = await res.json();
      if (!res.ok) {
        const errDetail = json.details ? JSON.stringify(json.details) : "";
        console.error("Save failed:", json, errDetail);
        setSaveMsg(`${json.message || json.error || "Kaydetme hatası"}${errDetail ? ` (${errDetail})` : ""}`);
        savedId = null;
      } else {
        if (!currentDraftId && json.data?.id) {
          savedId = json.data.id;
          setCurrentDraftId(json.data.id);
          // Kaynak tasarima bagla (Tasarimlar sayfasinda "Bagli Listeler" olarak gorunur)
          if (sourceDesignId) {
            try { await apiClient.linkDesignToListing(sourceDesignId, json.data.id); } catch { /* baglama kritik degil */ }
          }
          // URL'i guncelle ama react-router remount tetikleme (aksi halde
          // Etsy'ye gonderim sirasinda bilesen remount olup islem yarim kalir).
          window.history.replaceState(null, "", `/listing-builder?id=${json.data.id}`);
          setSaveMsg("Oluşturuldu");
        } else if (!silent) {
          setSaveMsg("Kaydedildi");
        }
        // Kaydedilen hal: sonrasinda degisiklik yoksa "kaydedildi" sayilir
        setSavedSig(savingSig);
        setLastSavedAt(new Date());
        videoRemovedRef.current = false;
        // Video istendiyse hazir olunca forma gelir (arka planda uretiliyor)
        const vid = json.data?.id ?? savedId;
        if (apiPayload.video && vid) watchDraftVideo(String(vid));
      }
    } catch (err) {
      console.error("Save error:", err);
      setSaveMsg("Kaydetme hatası");
      savedId = null;
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(null), 8000);
    return savedId;
  }

  // (Inventory building is now inline in handlePublishToEtsy with taxonomy-based property IDs)

  // Arka plan yayin job'unu izle: durum degistikce mesaji guncelle, bitince
  // Etsy ID'yi forma yaz. Sayfa acik oldugu surece takip eder (yayin zaten
  // sunucuda calisir, sayfa kapansa da tamamlanir).
  function startPublishPolling(draftId: string) {
    if (publishPollRef.current) clearTimeout(publishPollRef.current);
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/etsy/listings/publish-queue?draftId=${draftId}`, { headers: getAuthHeaders() });
        const json = await res.json();
        const job = (json.data ?? [])[0] as { status: string; state?: string; summary?: string; error?: string; etsyListingId?: string } | undefined;
        if (!job) return;
        if (job.status === "done") {
          if (job.etsyListingId) setCurrentEtsyListingId(String(job.etsyListingId));
          setListingActive(job.state === "active");
          setSaveMsg(job.summary || "Etsy'ye gönderildi (arka plan)");
          setTimeout(() => setSaveMsg(null), 12000);
          return;
        }
        if (job.status === "error") {
          setSaveMsg(`Yayın hatası: ${job.error || "bilinmeyen"}`);
          return;
        }
        if (job.status === "extension") {
          setSaveMsg("Eklentiyle yüklenmeyi bekliyor: eklentide Yükle sekmesinden Etsy formunu doldur.");
          return;
        }
        setSaveMsg(job.status === "processing" ? "Arka planda gönderiliyor..." : "Kuyrukta bekliyor...");
        publishPollRef.current = setTimeout(poll, 3000);
      } catch {
        publishPollRef.current = setTimeout(poll, 5000);
      }
    };
    publishPollRef.current = setTimeout(poll, 1500);
  }

  // Unmount'ta izleyiciyi durdur (yayin sunucuda devam eder)
  useEffect(() => () => { if (publishPollRef.current) clearTimeout(publishPollRef.current); }, []);

  // Taslak yuklendiginde aktif bir yayin job'i varsa durumu izlemeye devam et
  useEffect(() => {
    if (!currentDraftId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/etsy/listings/publish-queue?draftId=${currentDraftId}`, { headers: getAuthHeaders() });
        const json = await res.json();
        const job = (json.data ?? [])[0] as { status: string } | undefined;
        if (!cancelled && job && (job.status === "pending" || job.status === "processing")) {
          startPublishPolling(currentDraftId);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDraftId]);

  // Planlama panelini acinca mevcut planli listeleri getir
  useEffect(() => {
    if (!showSchedule) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/listing-drafts?light=1&status=scheduled`, { headers: getAuthHeaders() });
        const json = await res.json();
        const rows = (json.data ?? []) as Array<{ id: string; title: string; scheduledAt: string | null }>;
        if (!cancelled) {
          setScheduledList(
            rows.filter((r) => r.scheduledAt).sort((a, b) => +new Date(a.scheduledAt!) - +new Date(b.scheduledAt!)),
          );
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [showSchedule, saveMsg]);

  // Plani iptal et: scheduledAt temizle, taslaga dondur
  async function cancelSchedule() {
    updateForm({ scheduledAt: "" });
    setIsScheduled(false);
    setShowSchedule(false);
    if (currentDraftId) {
      try {
        await apiClient.updateListingDraft(currentDraftId, { scheduledAt: null, status: "draft" } as never);
        setSaveMsg("Plan iptal edildi");
      } catch { setSaveMsg("Plan iptal edilemedi"); }
      setTimeout(() => setSaveMsg(null), 4000);
    }
  }

  // ---- Publish to Etsy ----
  // Etsy'deki gorselleri silip taslaktaki TUM gorselleri (mockup + size chart) yeniden gonder
  // ── Tasarimi degistir: mockuplar yeni tasarimla yeniden uretilir, Etsy'de yenilenir ──
  const [redesignOpen, setRedesignOpen] = useState(false);
  const [redesignQuery, setRedesignQuery] = useState("");
  const [redesignList, setRedesignList] = useState<Array<{ id: string; name: string; sku: string | null; thumbnailUrl: string | null; originalUrl: string; editedUrl: string | null }>>([]);
  const [redesignBusy, setRedesignBusy] = useState<string | null>(null);
  // "Mockuplari yenile" penceresi: tablo ayari, mockup ekleme, sonra yenile
  const [refreshOpen, setRefreshOpen] = useState(false);
  // Pencerenin onizlemesi (basim yok): sonuc sirasi, korunan/cikan mockuplar, tablo kararlari
  type RefreshPlan = {
    mockups: Array<{ id: string; label: string; thumb: string | null; noArea: boolean; why: string }>;
    dropped: Array<{ id: string; label: string; thumb: string | null }>;
    manual: string[];
    template: Array<{ url: string; kind: "chart" | "info" | "other"; print: boolean; reason: string }>;
    total: number; overflow: number;
  };
  const [refreshPlan, setRefreshPlan] = useState<RefreshPlan | null>(null);
  const [planBusy, setPlanBusy] = useState(false);
  async function loadRefreshPlan(mode?: "auto" | "on" | "off") {
    if (!currentDraftId || linkedDesigns.length !== 1) return;
    setPlanBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/etsy/listings/refresh-plan`, {
        method: "POST", headers: getAuthHeaders(),
        body: JSON.stringify({ listingDraftId: currentDraftId, designId: linkedDesigns[0].id, chartMode: mode ?? form.attributes?.chartDesign ?? "auto" }),
      });
      const j = await r.json().catch(() => ({}));
      setRefreshPlan(r.ok ? j.data : null);
    } catch { setRefreshPlan(null); }
    setPlanBusy(false);
  }
  useEffect(() => { if (refreshOpen) void loadRefreshPlan(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshOpen]);
  const chartMode = form.attributes?.chartDesign ?? "auto";
  async function setChartMode(mode: "auto" | "on" | "off") {
    if (!currentDraftId) return;
    updateForm({ attributes: { ...form.attributes, chartDesign: mode } });
    void loadRefreshPlan(mode);
    try {
      const r = await fetch(`${API_BASE}/api/listing-drafts/${currentDraftId}/chart-design`, { method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ mode }) });
      if (!r.ok) throw new Error();
    } catch { setSaveMsg("Tablo ayarı kaydedilemedi"); }
  }
  // "Mockup ekle": kutuphaneden secilen mockup listenin tasarimiyla basilip taslaga eklenir
  const [addMockOpen, setAddMockOpen] = useState(false);
  const [addMockBusy, setAddMockBusy] = useState(false);
  async function handleAddMockup(mockupId: string) {
    if (!currentDraftId) return;
    setAddMockBusy(true);
    setSaveMsg("Mockup listenin tasarımıyla basılıyor…");
    try {
      const r = await fetch(`${API_BASE}/api/etsy/listings/add-mockups`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: currentDraftId, mockupIds: [mockupId] }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Mockup eklenemedi");
      updateForm({ imageUrls: j.data.imageUrls ?? [] });
      setSavedSig(null);
      if (refreshOpen) void loadRefreshPlan();
      if (currentEtsyListingId) setEtsyImagesPending(true);
      setSaveMsg(j.data.added
        ? `Mockup eklendi${j.data.truncated ? ` — 20 görsel sınırı: son ${j.data.truncated} görsel düştü` : ""}${currentEtsyListingId ? ". Etsy'ye \"Etsy'de güncelle\" ile gider" : ""}`
        : "Mockup basılamadı — mockup'ın tasarım alanı var mı kontrol et");
    } catch (e) {
      setSaveMsg(`Mockup ekleme hatası: ${e instanceof Error ? e.message : String(e)}`);
    } finally { setAddMockBusy(false); }
  }
  const [redesignChatgpt, setRedesignChatgpt] = useChatGptFilter();
  // "Yeni tasarim uret": listenin ilham analizinden Dengeli/Serbest tasarim; tiklaninca mockuplar yenilenir,
  // IP sIkI magazada baslik + etiketler yeni tasarima gore forma yazilir (kullanici kaydedip Etsy'ye gonderir)
  const { models: genModels, model: genModel, setModel: setGenModel } = useDesignModel("erc:model:design-workshop");
  const [genPrompt, setGenPrompt] = useState("");
  const [genFid, setGenFid] = useState<"balanced" | "loose">("balanced");
  const [genBusy, setGenBusy] = useState(false);
  const [genSeo, setGenSeo] = useState(true);
  const [genItems, setGenItems] = useState<Array<{ id: string; sku: string; status: "generating" | "ready" | "failed"; poolId?: string; url?: string; error?: string }>>([]);
  const genAnalysisId = inspirations[0]?.id ?? linkedDesigns.map((d) => d.metadata?.analysisId).find(Boolean) ?? null;
  useEffect(() => {
    if (!redesignOpen || !genAnalysisId || genPrompt) return;
    fetch(`${API_BASE}/api/analyses/${genAnalysisId}`, { headers: getAuthHeaders() })
      .then((r) => r.json()).then((j) => { if (j.data) setGenPrompt(buildBasePrompt(j.data)); }).catch(() => {});
  }, [redesignOpen, genAnalysisId]); // eslint-disable-line react-hooks/exhaustive-deps
  async function handleGenerateDesign() {
    if (!genAnalysisId || !genPrompt.trim()) return;
    setGenBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/analyses/${genAnalysisId}/designs`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ prompt: genPrompt.trim(), model: genModel, fidelity: genFid }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Tasarım üretilemedi");
      setGenItems((xs) => [{ id: j.data.id, sku: j.data.sku, status: "generating" }, ...xs]);
    } catch (e) {
      setGenItems((xs) => [{ id: `err-${Date.now()}`, sku: "", status: "failed", error: e instanceof Error ? e.message : String(e) }, ...xs]);
    } finally { setGenBusy(false); }
  }
  // Uretilenleri izle: tamamlaninca tasarim havuzundaki karsiligi (SKU ile) bulunur
  useEffect(() => {
    if (!genAnalysisId || !genItems.some((g) => g.status === "generating")) return;
    const t = setInterval(async () => {
      const next = await Promise.all(genItems.map(async (g) => {
        if (g.status !== "generating") return g;
        const d = await fetch(`${API_BASE}/api/analyses/${genAnalysisId}/designs/${g.id}`, { headers: getAuthHeaders() }).then((r) => r.json()).catch(() => null);
        if (d?.data?.status === "failed") return { ...g, status: "failed" as const, error: d.data.error ?? "Üretilemedi" };
        if (d?.data?.status !== "completed") return g;
        const pool = await fetch(`${API_BASE}/api/designs?limit=5&search=${encodeURIComponent(g.sku)}`, { headers: getAuthHeaders() }).then((r) => r.json()).catch(() => null);
        const hit = ((pool?.data ?? []) as Array<{ id: string; sku: string | null; editedUrl: string | null; originalUrl: string }>).find((x) => x.sku === g.sku);
        return hit ? { ...g, status: "ready" as const, poolId: hit.id, url: hit.editedUrl || hit.originalUrl } : g;
      }));
      setGenItems(next);
    }, 4000);
    return () => clearInterval(t);
  }, [genItems, genAnalysisId]);
  async function applyGenerated(poolId: string) {
    if (!(await handleRedesign(poolId)) || !genSeo || !activeShopInfo?.ipStrict || !currentDraftId) return;
    const r = await fetch(`${API_BASE}/api/listing-drafts/${currentDraftId}/design-seo`, { method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ designId: poolId }) });
    const j = await r.json().catch(() => ({}));
    if (j.data?.title) {
      updateForm({ title: j.data.title, tags: j.data.tags ?? form.tags });
      setSaveMsg("Tasarım değişti; başlık ve etiketler yeni tasarıma göre güncellendi. Kontrol et, kaydet ve Etsy'ye gönder; görseller için \"Etsy'de güncelle\"");
    }
  }
  useEffect(() => {
    if (!redesignOpen) return;
    const t = setTimeout(() => {
      const q = redesignQuery.trim();
      fetch(`${API_BASE}/api/designs?limit=24${q ? `&search=${encodeURIComponent(q)}` : ""}${redesignChatgpt !== "all" ? `&chatgpt=${redesignChatgpt}` : ""}`, { headers: getAuthHeaders() })
        .then((r) => r.json()).then((j) => setRedesignList((j.data ?? []) as typeof redesignList)).catch(() => setRedesignList([]));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redesignOpen, redesignQuery, redesignChatgpt]);
  /** refresh: ayni (bagli) tasarimla mockuplari yeniden uret — mockup ayari (renk etiketi vb.) degisince */
  async function handleRedesign(designId: string, refresh = false, confirmed = false): Promise<boolean> {
    if (!currentDraftId) return false;
    const onEtsy = !!currentEtsyListingId;
    // Etsy'ye hemen gitmez: yeni gorseller taslakta incelenir, "Etsy'de güncelle" ile onaylanir
    if (!confirmed && !confirm(refresh
      ? `Mockuplar güncel mockup ayarlarıyla (renk etiketi, koleksiyon vb.) yeniden üretilip taslağın görselleri değiştirilecek.${onEtsy ? " Etsy'ye, sen kontrol edip \"Etsy'de güncelle\"ye basınca gider." : ""} Devam edilsin mi?`
      : `Mockuplar bu tasarımla yeniden üretilip taslağın görselleri değiştirilecek.${onEtsy ? " Etsy'ye, sen kontrol edip \"Etsy'de güncelle\"ye basınca gider." : ""} Devam edilsin mi?`)) return false;
    setRedesignBusy(designId);
    setSaveMsg(refresh ? "Mockuplar yeniden üretiliyor… (bir-iki dakika sürebilir)" : "Mockuplar yeni tasarımla üretiliyor… (bir-iki dakika sürebilir)");
    try {
      const r = await fetch(`${API_BASE}/api/etsy/listings/redesign`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: currentDraftId, designId, resync: false }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Tasarım değiştirilemedi");
      // Koleksiyon disinda kalanlar: korunan (ana gorsel, satan, elle eklenen) ve cikarilan ek mockuplar
      const kept = (j.data?.kept ?? []) as string[], dropped = (j.data?.dropped ?? []) as string[];
      setSaveMsg(`${refresh ? "Mockuplar yenilendi" : "Tasarım değişti"} — ${j.data?.summary ?? ""}${j.data?.failed?.length ? ` · ${j.data.failed.length} mockup üretilemedi` : ""}`
        + (kept.length ? ` · Koleksiyonda olmayan ama korunan: ${kept.join(", ")}` : "")
        + (dropped.length ? ` · UYARI: koleksiyonda olmadığı için çıkarıldı: ${dropped.join(", ")} (gerekirse "Mockup ekle" ile geri ekle)` : "")
        + (onEtsy ? ". Görselleri kontrol et; uygunsa \"Etsy'de güncelle\"ye bas" : ""));
      if (onEtsy) setEtsyImagesPending(true);
      setRedesignOpen(false);
      // Yeni gorselleri ve baglantiyi yukle
      const d = await apiClient.getListingDraft(currentDraftId);
      updateForm({ imageUrls: d.imageUrls ?? [] });
      {
        const ds = ((d as unknown as { designLinks?: Array<{ design: LinkedDesign }> }).designLinks ?? []).map((l) => l.design).filter(Boolean);
        setLinkedDesigns(ds);
        setInspirations(inspirationsOf(d, ds));
      }
      setSavedSig(null);
      return true;
    } catch (e) {
      setSaveMsg(`Tasarım değiştirme hatası: ${e instanceof Error ? e.message : String(e)}`);
      return false;
    } finally {
      setRedesignBusy(null);
      setTimeout(() => setSaveMsg(null), 15000);
    }
  }

  async function handleResyncListingImages() {
    if (!currentDraftId || !currentEtsyListingId) return;
    if (!confirm("Etsy'deki mevcut görseller silinip taslaktaki tüm görseller (mockup + size chart) yeniden yüklenecek. Devam?")) return;
    setResyncingImgs(true);
    setSaveMsg("Görseller yenileniyor…");
    try {
      const res = await fetch(`${API_BASE}/api/etsy/listings/resync-images`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: currentDraftId }),
      });
      const json = await res.json();
      setSaveMsg(res.ok ? `Görseller yenilendi — ${json.data?.summary || ""}` : (json.error || "Görsel senkronu başarısız"));
      if (res.ok) setEtsyImagesPending(false);
    } catch { setSaveMsg("Görsel senkronu başarısız (bağlantı hatası)"); }
    setResyncingImgs(false);
  }

  // Yalniz video: gorsellere dokunmadan Etsy'deki videoyu yukler/degistirir
  async function handleResyncVideo() {
    if (!currentDraftId || !currentEtsyListingId || !form.videoUrl) return;
    if (/^(blob:|data:)/.test(form.videoUrl)) { setSaveMsg("Video henüz sunucuya yüklenmedi — biraz bekleyip tekrar deneyin"); return; }
    setVideoBusy(true);
    setSaveMsg("Video Etsy'ye gönderiliyor…");
    try {
      const res = await fetch(`${API_BASE}/api/etsy/listings/resync-video`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: currentDraftId, videoUrl: form.videoUrl }),
      });
      const json = await res.json();
      setSaveMsg(res.ok ? `Video güncellendi — ${json.data?.summary || ""}` : (json.error || "Video senkronu başarısız"));
    } catch { setSaveMsg("Video senkronu başarısız (bağlantı hatası)"); }
    setVideoBusy(false);
  }

  async function handlePublishToEtsy(asDraft = false, activate = false) {
    if (publishing) return; // cift gonderim engeli
    // Ortak kurallar: engel varsa Etsy'ye hic gidilmez; ilk eksige goturulur
    if (readiness.blockers.length) {
      const first = readiness.blockers[0];
      setSaveMsg(`Yayınlanamaz: ${first.message}${readiness.blockers.length > 1 ? ` (+${readiness.blockers.length - 1} eksik daha — üstteki listeye bakın)` : ""}`);
      goSection(first.section);
      setTimeout(() => setSaveMsg(null), 10000);
      return;
    }
    setPublishing(true);
    setSaveMsg(null);
    try {
      // Once tum formu kaydet (sku/profiller/materials vb. DB'ye yazilsin ki
      // Etsy'ye gonderdikten sonra taslak yeniden acilinca degerler kaybolmasin).
      // handleSave taslak id'sini dondurur; setState senkron olmadigindan
      // currentDraftId yerine bu id'yi kullaniyoruz (yeni taslakta stale kalmasin).
      const savedDraftId = (await handleSave()) ?? currentDraftId;
      // Create Etsy listing
      // Resolve readiness_state_id from processingProfileId (could be numeric ID from API or legacy string)
      let resolvedReadinessStateId: number | undefined;
      if (form.processingProfileId) {
        const numVal = Number(form.processingProfileId);
        if (!isNaN(numVal) && numVal > 0) {
          resolvedReadinessStateId = numVal;
        }
        // If it's a legacy string value like "made_to_order_1_3", don't send it - let backend auto-resolve
      }

      const isUpdate = !!currentEtsyListingId;

      // ===== ARKA PLAN KUYRUGU: yeni listeler icin. Draft'i kaydettik; sunucuda
      // bir yayin job'i olusturup HEMEN donuyoruz. Yayin (create+gorsel+envanter)
      // arka planda scheduler tarafindan tamamlanir — sayfadan ciksan bile devam eder.
      if (!isUpdate) {
        if (!savedDraftId) {
          setSaveMsg("Taslak kaydedilemedi, tekrar deneyin");
          setPublishing(false);
          return;
        }
        const state = activate ? "active" : "draft";
        try {
          const qRes = await fetch(`${API_BASE}/api/etsy/listings/publish-queue`, {
            method: "POST", headers: getAuthHeaders(),
            body: JSON.stringify({ listingDraftId: savedDraftId, state, title: form.title.trim() }),
          });
          const qJson = await qRes.json();
          if (!qRes.ok) {
            setSaveMsg(qJson.error || "Kuyruğa eklenemedi");
          } else {
            setSaveMsg(`Kuyruğa alındı — arka planda ${state === "active" ? "yayınlanıyor" : "taslak gönderiliyor"}. Sayfadan çıkabilirsin.`);
            startPublishPolling(savedDraftId);
          }
        } catch {
          setSaveMsg("Kuyruğa eklenemedi (bağlantı). Job oluşmuş olabilir — birazdan kontrol edin.");
        }
        setPublishing(false);
        setTimeout(() => setSaveMsg(null), 10000);
        return;
      }

      const etsyPayload = {
        title: form.title.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        quantity: Number(form.quantity) || 1,
        tags: form.tags.map(t => t.length > 20 ? t.slice(0, 20).trim() : t),
        taxonomy_id: form.taxonomyId ? Number(form.taxonomyId) : undefined,
        who_made: form.whoMade,
        when_made: form.whenMade,
        is_supply: form.isSupply,
        type: form.listingType,
        shipping_profile_id: form.shippingProfileId ? Number(form.shippingProfileId) : undefined,
        return_policy_id: form.returnPolicyId && form.returnPolicyId !== "no_returns" ? Number(form.returnPolicyId) : undefined,
        production_partner_ids: form.productionPartnerId ? [Number(form.productionPartnerId)] : undefined,
        readiness_state_id: resolvedReadinessStateId,
        sku: form.sku || undefined,
        should_auto_renew: form.autoRenew,
        shop_section_id: form.sectionId ? Number(form.sectionId) : undefined,
        materials: form.materials.length > 0 ? form.materials : undefined,
        personalization_questions: form.personalization.enabled && form.personalization.questions.length > 0 ? form.personalization.questions : undefined,
        ...(activate ? { state: "active" } : asDraft && !isUpdate ? { state: "draft" } : {}),
      };

      // ENVANTER gonderimi (varyasyon/SKU). Guncellemede LISTE PATCH'INDEN ONCE
      // cagrilir: Etsy liste guncellenirken MEVCUT envanteri dogruluyor; eski
      // 600 kombinasyonlu (fiyat tum eksenlerde, sinir 400) liste guncellemeyi
      // reddediyor ve yeni varyasyon yapisi hic gonderilemiyordu.
      // Envanter (varyasyon/fiyat/SKU) SUNUCUDAKI tek kurucuyla gonderilir —
      // yeni yayinla ayni kurallar (3 eksen, fiyat ekseni, 400/2500 tavani).
      // Taslak bu adimdan once kaydedildi; sunucu kayitli taslagi okur.
      const pushInventory = async (etsyListingId: string | number | undefined, _etsyReadinessStateId?: unknown): Promise<string> => {
        if (!etsyListingId || !savedDraftId || (form.variations.length === 0 && !form.sku)) return "";
        setSaveMsg(form.variations.length > 0 ? "Varyasyonlar gönderiliyor…" : "SKU gönderiliyor…");
        try {
          const r = await fetch(`${API_BASE}/api/etsy/listings/${etsyListingId}/inventory-from-draft`, {
            method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: savedDraftId }),
          });
          const j = await r.json().catch(() => ({}));
          return r.ok ? "" : String(j.error || j.details || "Envanter güncellenemedi");
        } catch (e) {
          return String(e);
        }
      };
      const isUpdateFirst = !!currentEtsyListingId;
      const preInventoryError = isUpdateFirst ? await pushInventory(currentEtsyListingId, resolvedReadinessStateId) : null;

      const sendUpdate = (extra: Record<string, unknown> = {}) => fetch(`${API_BASE}/api/etsy/listings/${currentEtsyListingId}`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...etsyPayload, ...extra }),
      });
      let res = isUpdate
        ? await sendUpdate()
        : await fetch(`${API_BASE}/api/etsy/listings`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(etsyPayload),
          });

      let json = await res.json();
      // Takida baslik tek seferde 60+ karakter kisaliyorsa sunucu durdurur;
      // kullanici bilerek onaylarsa yine de gonderilir
      if (isUpdate && res.status === 409 && json.code === "title_cut_too_large" && window.confirm(json.error)) {
        res = await sendUpdate({ allowLargeTitleCut: true });
        json = await res.json();
      }
      if (!res.ok) {
        const detail = json.details || json.data?.error || "";
        setSaveMsg(`${json.error || (isUpdate ? "Etsy güncelleme hatası" : "Etsy'ye gönderim hatası")}${detail ? `: ${detail}` : ""}${preInventoryError ? ` — önce gönderilen yeni envanter de reddedildi: ${preInventoryError}` : ""}`);
      } else {
        const etsyListingId = isUpdate ? currentEtsyListingId : json.data?.listing_id;
        const persWarning: string = json.personalizationWarning || "";
        if (persWarning) console.warn(persWarning);
        const etsyReadinessStateId = json.data?.readiness_state_id || resolvedReadinessStateId;
        // Etsy'deki GERCEK durum: aktiflestirildiyse ya da zaten aktifse yayinda;
        // aksi halde Etsy'de taslak -> panelde de taslak
        const newStatus = !asDraft && (activate || (isUpdate && listingActive)) ? "published" : "draft";
        // Update draft with etsy listing id
        if (etsyListingId) {
          setCurrentEtsyListingId(String(etsyListingId));
          if (activate) setListingActive(true); // Yayınla -> active
          if (savedDraftId) await apiClient.updateListingDraft(savedDraftId, { etsyListingId: String(etsyListingId), status: newStatus as ListingDraft["status"] });
        }

        // Upload images + video to Etsy via server-side batch endpoint
        // Server fetches images from R2/local directly — no frontend fetch issues
        let uploadedCount = 0;
        let failedCount = 0;
        const savedVideoUrl = form.videoUrl;

        // On update, skip images already hosted on Etsy (avoid duplicating them);
        // only push newly-added images.
        const imagesToUpload = isUpdate ? form.imageUrls.filter((u) => !u.includes("etsystatic.com")) : form.imageUrls;
        // GUNCELLEMEDE gorseller EKLENMEZ, ESITLENIR: ilk yayin kuyruktan yapildiysa
        // taslaktaki adresler Etsy CDN adresi degil, hepsi "yeni" sanilip tekrar
        // ekleniyordu — 20 sinirina takilip cogu reddediliyor, kopyalar kaliyordu.
        // resync: eksikler yuklenir, fazlalar/kopyalar silinir, video yoksa eklenir.
        if (isUpdate && etsyListingId && savedDraftId && form.imageUrls.length > 0) {
          setSaveMsg(`Güncellendi. Görseller${form.videoUrl ? " ve video" : ""} eşitleniyor...`);
          try {
            const rs = await fetch(`${API_BASE}/api/etsy/listings/resync-images`, {
              method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ listingDraftId: savedDraftId }),
            });
            const rj = await rs.json().catch(() => ({}));
            if (rs.ok) {
              uploadedCount = rj.data?.uploaded ?? 0;
              failedCount = rj.data?.failed ?? 0;
              setSaveMsg(rj.data?.summary || `${uploadedCount} görsel eşitlendi`);
            } else {
              setSaveMsg(`Görsel eşitleme hatası: ${rj.error || rs.status}`);
            }
          } catch (e) {
            console.error("Resync error:", e);
            setSaveMsg("Görsel eşitleme bağlantı hatası");
          }
        } else if (etsyListingId && imagesToUpload.length > 0) {
          setSaveMsg(`${asDraft ? "Taslak oluşturuldu" : isUpdate ? "Güncellendi" : "Listing oluşturuldu"}. Görseller${form.videoUrl ? " ve video" : ""} yükleniyor...`);
          try {
            const batchRes = await fetch(`${API_BASE}/api/etsy/images/${etsyListingId}/upload-from-urls`, {
              method: "POST",
              headers: getAuthHeaders(),
              body: JSON.stringify({
                imageUrls: imagesToUpload,
                videoUrl: form.videoUrl || undefined,
              }),
            });
            if (batchRes.ok) {
              const batchJson = await batchRes.json();
              const results = batchJson.data?.images || [];
              uploadedCount = results.filter((r: { success: boolean }) => r.success).length;
              failedCount = results.filter((r: { success: boolean }) => !r.success).length;
              if (failedCount > 0) {
                const failedDetails = results.filter((r: { success: boolean }) => !r.success).map((r: { rank: number; error?: string }) => `#${r.rank}: ${r.error || "?"}`).join(", ");
                console.warn("Failed uploads:", failedDetails);
              }
              setSaveMsg(batchJson.data?.summary || `${uploadedCount} görsel yüklendi`);
            } else {
              const errJson = await batchRes.json().catch(() => ({}));
              setSaveMsg(`Görsel yükleme hatası: ${errJson.error || batchRes.status}`);
            }
          } catch (e) {
            console.error("Batch upload error:", e);
            setSaveMsg("Görsel yükleme bağlantı hatası");
          }
        }
        if (etsyListingId && uploadedCount > 0 && savedDraftId) {
          try {
            setSaveMsg("Etsy görsel URL'leri alınıyor...");
            const imgListRes = await fetch(`${API_BASE}/api/etsy/listings/${etsyListingId}`, {
              headers: getAuthHeaders(),
            });
            if (imgListRes.ok) {
              const imgListJson = await imgListRes.json();
              const etsyImages = imgListJson.data?.images || [];
              if (etsyImages.length > 0) {
                const cdnUrls = etsyImages
                  .sort((a: { rank: number }, b: { rank: number }) => a.rank - b.rank)
                  .map((img: { url_fullxfull: string }) => img.url_fullxfull)
                  .filter(Boolean);
                if (cdnUrls.length > 0) {
                  updateForm({ imageUrls: cdnUrls, videoUrl: savedVideoUrl || "" });
                  await apiClient.updateListingDraft(savedDraftId, { imageUrls: cdnUrls });
                }
              }
            }
          } catch (e) {
            console.warn("Could not fetch Etsy CDN URLs:", e);
          }
        }

        // Envanter: guncellemede PATCH'ten once gonderildi, olusturmada simdi
        const inventoryError = preInventoryError ?? await pushInventory(etsyListingId, etsyReadinessStateId);

        // Final status message
        if (inventoryError) {
          setSaveMsg(`Listing oluşturuldu (${uploadedCount} görsel) ama envanter gönderilemedi: ${inventoryError}`);
        } else if (etsyListingId) {
          const parts = [];
          parts.push(isUpdate ? "Güncellendi!" : `${asDraft ? "Taslak" : "Listing"} oluşturuldu!`);
          if (form.imageUrls.length > 0) parts.push(`${uploadedCount}/${form.imageUrls.length} görsel yüklendi${failedCount > 0 ? ` (${failedCount} başarısız)` : ""}.`);
          if (form.videoUrl) parts.push("Video yüklendi.");
          if (form.variations.length > 0) parts.push(`${form.variations.length} varyasyon gönderildi.`);
          if (form.sku) parts.push(`SKU: ${form.sku}`);
          if (persWarning) parts.push(`⚠️ ${persWarning}`);
          else if (form.personalization.enabled && form.personalization.questions.length > 0) parts.push(`${form.personalization.questions.length} kişiselleştirme sorusu eklendi.`);
          setSaveMsg(parts.join(" "));
        } else {
          setSaveMsg(asDraft ? `Etsy'ye taslak gönderildi!` : `Etsy'ye gönderildi!`);
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Ag hatasinda istek Etsy'ye ulasip listing OLUSTURMUS olabilir — tekrar denemeden once kontrol
      setSaveMsg(`Etsy bağlantı hatası: ${msg}. İstek gönderilmiş OLABİLİR — tekrar denemeden önce Etsy > Taslaklar'i kontrol edin (çift kayıt oluşmasın).`);
    }
    setPublishing(false);
    setTimeout(() => setSaveMsg(null), 15000);
  }

  // Kopyala: secilen her kargo profili icin bir kopya; SKU'nun zaten
  // listelendigi profiller secilemez (sunucu da reddeder)
  const [dupOpen, setDupOpen] = useState(false);
  const [dupUsed, setDupUsed] = useState<string[]>([]);
  const [dupPick, setDupPick] = useState<string[]>([]);
  const [dupBusy, setDupBusy] = useState(false);
  async function openDuplicate() {
    setDupPick([]);
    setDupUsed(form.shippingProfileId ? [form.shippingProfileId] : []);
    setDupOpen(true);
    if (!form.sku.trim()) return;
    const r = await fetch(`${API_BASE}/api/listing-drafts/sku-profiles?sku=${encodeURIComponent(form.sku.trim())}`, { headers: getAuthHeaders() }).catch(() => null);
    const used = r?.ok ? ((await r.json()).data ?? []) as string[] : [];
    setDupUsed((cur) => [...new Set([...cur, ...used])]);
  }
  async function handleDuplicate() {
    if (!currentDraftId || !dupPick.length) return;
    setDupBusy(true);
    const made: string[] = [];
    const errors: string[] = [];
    for (const pid of dupPick) {
      const res = await fetch(`${API_BASE}/api/listing-drafts/${currentDraftId}/duplicate`, {
        method: "POST", headers: getAuthHeaders(), body: JSON.stringify({ shippingProfileId: pid }),
      }).catch(() => null);
      const data = await res?.json().catch(() => ({}));
      if (data?.data?.id) made.push(data.data.id);
      else errors.push(data?.message || "kopya oluşturulamadı");
    }
    setDupBusy(false);
    setDupOpen(false);
    setSaveMsg(errors.length ? `${made.length} kopya oluşturuldu — hata: ${errors.join(" · ")}` : `${made.length} kopya oluşturuldu`);
    setTimeout(() => setSaveMsg(null), 8000);
    if (made.length === 1 && !errors.length) navigate(`/listing-builder?id=${made[0]}`);
  }

  // ---- Save as Template ----
  async function handleSaveAsTemplate() {
    const name = prompt("Şablon adı girin:");
    if (!name?.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/api/listing-templates`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: name.trim(),
          description: form.description,
          tags: form.tags,
          category: form.category || null,
          price: form.price ? Number(form.price) : null,
          sku: form.sku || null,
          whoMade: form.whoMade,
          whatIsIt: form.whatIsIt,
          whenMade: form.whenMade,
          isSupply: form.isSupply,
          isDigital: form.isDigital,
          autoRenew: form.autoRenew,
          isFeatured: form.isFeatured,
          shippingProfileId: form.shippingProfileId || null,
          taxonomyId: form.taxonomyId ? Number(form.taxonomyId) : null,
          quantity: Number(form.quantity) || 1,
          listingType: form.listingType,
          variations: form.variations.length > 0 ? form.variations : null,
          disabledCombos: form.disabledCombos.length > 0 ? form.disabledCombos : null,
          personalization: form.personalization.enabled ? form.personalization : null,
          imageUrls: form.imageUrls,
          videoUrl: form.videoUrl || null,
          materials: form.materials,
          attributes: (() => {
            // Taslaga ozel sistem alanlari (tedarikci urunu, fiyatlama) sablona yazilmaz
            const a: Record<string, unknown> = withoutSystemAttrs(form.attributes);
            if (form.imageLabels.length > 0) a.imageLabels = form.imageLabels;
            return Object.keys(a).length > 0 ? a : null;
          })(),
          sectionId: form.sectionId || null,
          processingProfileId: form.processingProfileId || null,
          productionPartnerId: form.productionPartnerId || null,
          returnPolicyId: form.returnPolicyId || null,
        }),
      });
      if (res.ok) {
        setSaveMsg("Şablon olarak kaydedildi!");
      } else {
        setSaveMsg("Şablon kaydetme hatası");
      }
    } catch { setSaveMsg("Şablon kaydetme hatası"); }
    setTimeout(() => setSaveMsg(null), 3000);
  }

  // ---- Helpers ----
  function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t) && form.tags.length < 13) { updateForm({ tags: [...form.tags, t] }); setTagInput(""); }
  }
  function addMaterial() {
    const m = materialInput.trim();
    if (m && !form.materials.includes(m) && form.materials.length < 13) { updateForm({ materials: [...form.materials, m] }); setMaterialInput(""); }
  }

  // Tablo ("chart" etiketli) gorseller 5. siradan itibaren (takida sona) — alici renk tablosunu gormeden seciyordu
  function sortImagesChartLast(urls: string[], labels: string[]): { imageUrls: string[]; imageLabels: string[] } {
    return placeChartImages(urls, labels, isJewelryForm ? "jewelry" : formVertical);
  }


  function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const url = ev.target?.result as string;
        setForm((prev) => {
          const newUrls = [...prev.imageUrls, url].slice(0, 20);
          const newLabels = [...prev.imageLabels, ""].slice(0, 20);
          return { ...prev, ...sortImagesChartLast(newUrls, newLabels) };
        });
      };
      reader.readAsDataURL(files[i]);
    }
    if (imageFileRef.current) imageFileRef.current.value = "";
  }


  function removeImage(index: number) {
    updateForm({ imageUrls: form.imageUrls.filter((_, i) => i !== index), imageLabels: form.imageLabels.filter((_, i) => i !== index) });
  }

  function reorderImages(from: number, to: number) {
    if (from === to || from < 0 || to < 0 || from >= form.imageUrls.length || to >= form.imageUrls.length) return;
    const urls = [...form.imageUrls]; const labels = [...form.imageLabels];
    const [u] = urls.splice(from, 1); urls.splice(to, 0, u);
    const [l] = labels.splice(from, 1); labels.splice(to, 0, l);
    updateForm({ imageUrls: urls, imageLabels: labels });
  }

  function setImageLabel(index: number, label: string) {
    const labels = [...form.imageLabels];
    while (labels.length <= index) labels.push("");
    labels[index] = label;
    const sorted = sortImagesChartLast([...form.imageUrls], labels);
    updateForm(sorted);
  }

  // Urun fotograflarindan slayt videosu (tisort akisiyla ayni ffmpeg slayti);
  // kaydedince taslaga yazilir, Etsy'ye gonderimde video olarak yuklenir.
  const [videoBusy, setVideoBusy] = useState(false);
  async function handleGenerateVideo() {
    const imgs = form.imageUrls.filter((u) => u && !u.startsWith("blob:") && !u.startsWith("data:")).slice(0, 6);
    if (!imgs.length) { setSaveMsg("Video için önce ürün fotoğrafı ekleyin"); return; }
    setVideoBusy(true);
    setSaveMsg("Video oluşturuluyor… (fotoğraf başına birkaç saniye)");
    try {
      const base = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
      const abs = imgs.map((u) => (/^https?:/.test(u) ? u : `${base}${u.startsWith("/") ? "" : "/"}${u}`));
      const r = await apiClient.generateSlideshowVideo({ imageUrls: abs, aspectRatio: "1:1", duration: 8 });
      updateForm({ videoUrl: r.videoUrl });
      setSaveMsg("Video oluşturuldu — kaydedince taslağa yazılır, Etsy'ye gönderince yüklenir");
    } catch (err) {
      setSaveMsg(`Video oluşturulamadı: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setVideoBusy(false);
    }
  }

  // Video sunucuya yuklenir ve KALICI adresi kaydedilir (gecici blob: adresi
  // yenilemede ve Etsy yayininda kayboluyordu)
  async function handleVideoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (videoFileRef.current) videoFileRef.current.value = "";
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { setSaveMsg("Video 100 MB'dan büyük olamaz"); return; }
    setSaveMsg("Video yükleniyor…");
    try {
      const fd = new FormData();
      fd.append("video", file);
      const headers = { ...getAuthHeaders() } as Record<string, string>;
      delete headers["Content-Type"];
      const r = await fetch(`${API_BASE}/api/listing-drafts/upload-video`, { method: "POST", headers, body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.data?.url) throw new Error(j.error || "Video yüklenemedi");
      updateForm({ videoUrl: j.data.url as string });
      setSaveMsg("Video yüklendi — kaydetmeyi unutmayın");
    } catch (err) {
      setSaveMsg(`Video yükleme hatası: ${err instanceof Error ? err.message : String(err)}`);
    }
    setTimeout(() => setSaveMsg(null), 6000);
  }

  // ---- Variations ----
  // Etsy: en fazla 3 eksen; tavan fiyat yapisina gore 2500 ya da 400 (ortak kural)
  const MAX_VARIATION_TYPES = 3;
  const MAX_OPTIONS_PER_VARIATION = 70;
  const MAX_TOTAL_COMBOS = listingComboCap(form.variations);
  function addVariation() {
    if (form.variations.length >= MAX_VARIATION_TYPES) return;
    const used = new Set(form.variations.map((v) => v.name));
    const name = VARIATION_NAME_SUGGESTIONS.find((n) => !used.has(n)) ?? "Option";
    updateForm({ variations: [...form.variations, { name, options: [{ label: "" }] }] });
  }
  function removeVariation(vi: number) { updateForm({ variations: form.variations.filter((_, i) => i !== vi) }); }
  function updateVariationName(vi: number, name: string) { const vars = [...form.variations]; vars[vi] = { ...vars[vi], name }; updateForm({ variations: vars }); }
  function addOption(vi: number) {
    const label = (newOptionInputs[vi] || "").trim();
    if (!label) return;
    if (form.variations[vi].options.length >= MAX_OPTIONS_PER_VARIATION) return;
    // Check total combos
    const otherCount = form.variations.filter((_, i) => i !== vi).reduce((s, v) => s * Math.max(1, v.options.length), 1);
    if ((form.variations[vi].options.length + 1) * otherCount > MAX_TOTAL_COMBOS) return;
    const vars = [...form.variations]; vars[vi] = { ...vars[vi], options: [...vars[vi].options, { label }] };
    updateForm({ variations: vars }); setNewOptionInputs({ ...newOptionInputs, [vi]: "" });
  }
  function removeOption(vi: number, oi: number) { const vars = [...form.variations]; vars[vi] = { ...vars[vi], options: vars[vi].options.filter((_, i) => i !== oi) }; updateForm({ variations: vars }); }
  function updateOptionLabel(vi: number, oi: number, label: string) {
    const vars = [...form.variations]; const opts = [...vars[vi].options]; opts[oi] = { ...opts[oi], label }; vars[vi] = { ...vars[vi], options: opts }; updateForm({ variations: vars });
  }
  /** Secenegin SABIT satis fiyati (takida fiyat ekseni: "3 Hearts" = $32).
   *  Bos birakilirsa secenek fiyati tabandan + ek fiyattan gelir. */
  function updateOptionSellingPrice(vi: number, oi: number, value: string) {
    const vars = [...form.variations]; const opts = [...vars[vi].options];
    const n = Number(value);
    const next = { ...opts[oi] } as VariationOption & { sellingPrice?: number };
    if (value.trim() === "" || !(n > 0)) delete next.sellingPrice; else next.sellingPrice = Math.round(n * 100) / 100;
    opts[oi] = next; vars[vi] = { ...vars[vi], options: opts }; updateForm({ variations: vars });
  }
  function updateOptionPrice(vi: number, oi: number, priceAdjust: number) {
    const vars = [...form.variations]; const opts = [...vars[vi].options]; opts[oi] = { ...opts[oi], priceAdjust }; vars[vi] = { ...vars[vi], options: opts }; updateForm({ variations: vars });
  }

  function applyVarTemplate(t: VarTemplate) {
    // Baz fiyati sablonun EN DUSUK sellingPrice'ina ayarla ki her hucre
    // (baz + priceAdjust) sablonda girilen fiyata birebir esit olsun.
    const selling = t.variations
      .flatMap((v) => v.options.map((o) => (o as VariationOption & { sellingPrice?: number }).sellingPrice))
      .filter((p): p is number => typeof p === "number" && p > 0);
    const base = selling.length ? Math.min(...selling) : null;
    updateForm({
      variations: t.variations,
      disabledCombos: t.disabledCombos ?? [],
      ...(base != null ? { price: String(base) } : {}),
    });
  }

  async function applyListingTemplate(t: ListingTemplateInfo) {
    try { localStorage.setItem("erc_last_listing_template", t.id); } catch { /* ignore */ }
    // Mevcut draft SKU'larini topla (ayni SKU'nun tekrar gelmesini engellemek icin)
    const existingSkus = new Set<string>();
    let drafts: { sku?: string }[] = [];
    try {
      const res = await fetch(`${API_BASE}/api/listing-drafts`, { headers: getAuthHeaders() });
      const json = await res.json();
      drafts = (json.data ?? []) as { sku?: string }[];
      for (const d of drafts) if (d.sku) existingSkus.add(d.sku.toLowerCase());
    } catch { /* ignore */ }

    // Tasarimin SKU'su oncelikli; yoksa listing sablonundaki SKU tabanini otomatik numarala.
    let finalSku = "";
    if (sourceDesignSku) {
      finalSku = nextFreeSku(sourceDesignSku, existingSkus, true);
    } else {
      const templateSku = t.sku || "";
      const skuBase = templateSku.replace(/-+$/, "");
      if (skuBase) {
        let nextSkuNum = 1;
        const pattern = new RegExp(`^${skuBase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}-(\\d+)$`, "i");
        for (const d of drafts) {
          if (d.sku) {
            const m = d.sku.match(pattern);
            if (m) { const n = parseInt(m[1], 10); if (n >= nextSkuNum) nextSkuNum = n + 1; }
          }
        }
        finalSku = `${skuBase}-${nextSkuNum}`;
      }
    }

    setForm((prev) => {
      // Merge images: keep existing images, add only new ones from template (no duplicates)
      const existingUrls = new Set(prev.imageUrls);
      const newTemplateUrls = (t.imageUrls || []).filter((u) => !existingUrls.has(u));
      const newTemplateLabels = (t.attributes?.imageLabels as string[]) || [];
      // Map labels for only the new urls
      const templateUrlsAll = t.imageUrls || [];
      const labelsForNew = newTemplateUrls.map((u) => {
        const idx = templateUrlsAll.indexOf(u);
        // Etiketsiz sablon gorseli de tablo sayilir (5. siraya yerlesir)
        return idx >= 0 && newTemplateLabels[idx] ? newTemplateLabels[idx] : "chart";
      });
      const mergedUrls = [...prev.imageUrls, ...newTemplateUrls].slice(0, 20);
      const mergedLabels = [...prev.imageLabels, ...labelsForNew].slice(0, 20);

      // Baz fiyati: sablonun varyasyon opsiyonlarinda bedene sabit fiyat
      // (sellingPrice) varsa EN DUSUK olani baz al (applyVarTemplate ile ayni),
      // boylece her hucre = o bedenin gercek fiyati olur; yoksa sablon fiyati.
      const tplSelling = (((t.variations as Variation[] | null) || []))
        .flatMap((v) => v.options.map((o) => (o as VariationOption & { sellingPrice?: number }).sellingPrice))
        .filter((p): p is number => typeof p === "number" && p > 0);
      const derivedBase = tplSelling.length ? Math.min(...tplSelling) : null;

      return {
        ...prev,
        title: prev.title || "",
        description: t.description || prev.description,
        tags: t.tags.length > 0 ? t.tags.slice(0, 13) : prev.tags,
        category: t.category || prev.category,
        // Takida fiyat urunun maliyetinden hesaplandi (attributes.pricing) — sablon ezmez
        price: prev.attributes?.pricing ? prev.price
          : derivedBase != null ? String(derivedBase) : (t.price ? String(t.price) : prev.price),
        sku: finalSku || prev.sku,
        quantity: String(t.quantity || 999),
        whoMade: t.whoMade || "i_did",
        whatIsIt: t.whatIsIt || "finished_product",
        whenMade: t.whenMade || "made_to_order",
        isSupply: t.isSupply,
        isDigital: t.isDigital ?? false,
        autoRenew: t.autoRenew ?? true,
        isFeatured: t.isFeatured ?? false,
        listingType: t.listingType || "physical",
        shippingProfileId: t.shippingProfileId || prev.shippingProfileId,
        taxonomyId: t.taxonomyId ? String(t.taxonomyId) : prev.taxonomyId,
        sectionId: t.sectionId || prev.sectionId,
        processingProfileId: t.processingProfileId || prev.processingProfileId,
        productionPartnerId: t.productionPartnerId || prev.productionPartnerId,
        returnPolicyId: t.returnPolicyId || prev.returnPolicyId,
        ...sortImagesChartLast(mergedUrls, mergedLabels),
        videoUrl: t.videoUrl || prev.videoUrl,
        // Takida varyasyonlar urunun fiyat ekseninden kuruldu — dolu ise korunur
        variations: prev.attributes?.pricing && prev.variations.length > 0
          ? prev.variations : ((t.variations as Variation[]) || prev.variations),
        disabledCombos: (t.disabledCombos as Record<string, string>[]) ?? prev.disabledCombos,
        materials: t.materials.length > 0 ? t.materials : prev.materials,
        // Taslagin sistem alanlari (taki kaynagi, fiyatlama) sablondan EZILMEZ
        attributes: {
          ...prev.attributes,
          ...withoutSystemAttrs((t.attributes as Record<string, unknown>) || {}),
          ...Object.fromEntries(SYSTEM_ATTR_KEYS.filter((k) => prev.attributes?.[k] !== undefined).map((k) => [k, prev.attributes[k]])),
        } as TemplateAttributes,
        personalization: t.personalization ? normalizePersonalization(t.personalization) : prev.personalization,
      };
    });
  }

  // ---- AI Content Generation ----
  async function generateAiContent(target: "title" | "description" | "tags" | "all") {
    setAiGenerating((prev) => ({ ...prev, [target]: true }));
    try {
      // Build keyword: use prompt if filled, otherwise use existing form data as context
      let keyword = aiPrompt.trim();
      if (!keyword) {
        keyword = form.tags.slice(0, 3).join(" ") || form.category || "etsy product";
      }

      // Include design analysis context if available from studio
      const daCtx = (window as unknown as Record<string, unknown>).__designAnalysisContext as StudioExportData["designAnalysis"] | undefined;

      const res = await fetch(`${API_BASE}/api/listing-builder/seo-generate`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          keyword,
          category: form.category || undefined,
          productType: form.whatIsIt || undefined,
          originalTitle: originalTitle || undefined,
          existingDescription: form.description.trim() || undefined,
          existingTags: form.tags.length > 0 ? form.tags : undefined,
          existingTitle: form.title.trim() || undefined,
          designAnalysis: daCtx || undefined,
          conceptId: selectedConceptId || undefined,
          templateId: appliedListingTemplateId || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setSaveMsg(json.error || "AI oluşturma hatası");
        return;
      }

      const generated = json.data?.generated;
      if (!generated) {
        setSaveMsg("AI yanıt parse edilemedi");
        return;
      }

      // Apply generated content based on target
      if (target === "title" || target === "all") {
        if (generated.title) updateForm({ title: generated.title });
      }
      if (target === "description" || target === "all") {
        if (generated.description) updateForm({ description: generated.description });
      }
      if (target === "tags" || target === "all") {
        if (generated.tags && Array.isArray(generated.tags)) {
          updateForm({ tags: generated.tags.slice(0, 13) });
        }
      }
      if (target === "all" && generated.suggestedPrice && !form.price) {
        updateForm({ price: String(generated.suggestedPrice) });
      }

      // Konsept bagli Magaza Bolumu otomatik doldurma (istege bagli)
      const conceptInfo = json.data?.concept as { defaultSectionId?: string | null; defaultSectionTitle?: string | null } | undefined;
      if (conceptAutofillSection && conceptInfo) {
        let secId = conceptInfo.defaultSectionId ? String(conceptInfo.defaultSectionId) : "";
        // ID yoksa baslikla mevcut Etsy bolumlerinden eslesme ara
        if (!secId && conceptInfo.defaultSectionTitle && shopSections) {
          const m = shopSections.find((s) => s.title.toLowerCase() === conceptInfo.defaultSectionTitle!.trim().toLowerCase());
          if (m) secId = String(m.shop_section_id);
        }
        if (secId) updateForm({ sectionId: secId });
      }

      setSaveMsg(target === "all" ? "AI ile tüm alanlar oluşturuldu" : `AI ile ${target === "title" ? "başlık" : target === "description" ? "açıklama" : "etiketler"} oluşturuldu`);
      // Close AI input panels after generation
      setShowAiInput({});
    } catch {
      setSaveMsg("AI bağlantı hatası");
    } finally {
      setAiGenerating((prev) => ({ ...prev, [target]: false }));
      setTimeout(() => setSaveMsg(null), 3000);
    }
  }

  // ---- Total variation count ----
  const totalVariationOptions = useMemo(() => form.variations.reduce((s, v) => s + v.options.length, 0), [form.variations]);

  // ---- Combo grid: Size x Color combinations for Etsy ----
  const variationCombos = useMemo(() => {
    if (form.variations.length < 2) return null;
    const v1 = form.variations[0];
    const v2 = form.variations[1];
    const totalCombos = v1.options.length * v2.options.length;
    return { v1Name: v1.name, v2Name: v2.name, count: totalCombos };
  }, [form.variations]);

  // En ucuz varyasyon (attributes.comboPrices): yayinda bu kombinasyonlar ozel
  // fiyatla gider; tabloda da o fiyat gosterilir ve buradan duzenlenir.
  function comboOverrideFor(labels: Record<string, string>) {
    return (comboPricesAttr ?? []).find((c) =>
      Object.entries(c.match).every(([k, v]) => labels[k] === undefined || labels[k] === v)
      && Object.entries(labels).every(([k, v]) => c.match[k] === undefined || c.match[k] === v));
  }
  function setComboOverride(labels: Record<string, string>, price: number) {
    setForm((prev) => {
      const list = ((prev.attributes?.comboPrices ?? []) as unknown as Array<{ match: Record<string, string>; price: number }>)
        .map((c) => (Object.entries(labels).every(([k, v]) => c.match[k] === v) ? { ...c, price: Math.round(price * 100) / 100 } : c));
      // attributes tipi metin alanlari icin; sistem alani (comboPrices) JSON olarak tasinir
      return { ...prev, attributes: { ...prev.attributes, comboPrices: list as unknown as string[] } };
    });
  }

  function updateComboPrice(v1Idx: number, v2Idx: number, newTotal: number) {
    const basePrice = Number(form.price) || 0;
    const opt1 = form.variations[0].options[v1Idx] as VariationOption & { sellingPrice?: number };
    const opt2 = form.variations[1].options[v2Idx] as VariationOption & { sellingPrice?: number };
    if (opt2.sellingPrice && opt2.sellingPrice > 0) {
      // 2. eksenin sabit fiyati var: o fiyat guncellenir (ortak kural: sabit fiyat + digerinin ek fiyati)
      const other = opt1.sellingPrice && opt1.sellingPrice > 0 ? 0 : (opt1.priceAdjust || 0);
      updateOptionSellingPrice(1, v2Idx, String(Math.round((newTotal - other) * 100) / 100));
      return;
    }
    // Fark v2 secenegine EK fiyat olarak yazilir (1. eksenin fiyati sabit kalir)
    const withoutV2 = comboSellingPrice([opt1], basePrice);
    updateOptionPrice(1, v2Idx, Math.round((newTotal - withoutV2) * 100) / 100);
  }

  // Build a map of section key -> order index for CSS order
  const sectionCssOrder = useMemo(() => {
    const map: Record<string, number> = {};
    sectionOrder.forEach((k, i) => { map[k] = i; });
    return map;
  }, [sectionOrder]);

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;
  }

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Left Sidebar */}
      <div className="w-56 border-r border-gray-200 bg-white flex-shrink-0 overflow-y-auto">
        <div className="p-4">
          <Link to="/listing-builder" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4">
            <ArrowLeft className="h-4 w-4" /> Listeler
          </Link>

          {/* Score */}
          <div className="mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-semibold text-gray-400 uppercase">Puan</span>
              <span className={`text-sm font-bold px-2 py-0.5 rounded-lg ${scoreColor(score)}`}>{score}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className={`h-1.5 rounded-full transition-all ${score >= 80 ? "bg-emerald-500" : score >= 60 ? "bg-blue-500" : score >= 40 ? "bg-amber-500" : "bg-red-500"}`}
                style={{ width: `${score}%` }} />
            </div>
          </div>

          <h2 className="text-sm font-bold text-gray-900 mb-4">Bölümler</h2>
          <nav className="space-y-0.5">
            {sectionOrder.map((key, idx) => {
              const sec = SECTION_MAP[key];
              if (!sec) return null;
              const count = sectionCount(form, key);
              const isActive = activeSection === key;
              return (
                <div key={key} className="group flex items-center">
                  {/* Reorder arrows */}
                  <div className="flex flex-col mr-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); moveSectionOrder(idx, idx - 1); }} disabled={idx === 0}
                      className="p-0 text-gray-300 hover:text-gray-500 disabled:opacity-20"><ChevronUp className="h-3 w-3" /></button>
                    <button onClick={(e) => { e.stopPropagation(); moveSectionOrder(idx, idx + 1); }} disabled={idx === sectionOrder.length - 1}
                      className="p-0 text-gray-300 hover:text-gray-500 disabled:opacity-20"><ChevronDown className="h-3 w-3" /></button>
                  </div>
                  <button onClick={() => { setActiveSection(key); sectionRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                    className={`flex-1 flex items-center justify-between px-2 py-2 text-sm rounded-lg transition-colors ${
                      isActive ? "text-indigo-600 bg-indigo-50 font-medium border-l-2 border-indigo-500" : "text-gray-600 hover:bg-gray-50 border-l-2 border-transparent"
                    }`}>
                    <span className="flex items-center gap-1.5">
                      {sec.label}
                      {blockedSections.has(key as ReadinessSection) && <span className="h-1.5 w-1.5 rounded-full bg-red-500" title="Bu bölümde eksik var" />}
                    </span>
                    <span className={`text-xs ${isActive ? "text-indigo-500" : "text-gray-400"}`}>({count})</span>
                  </button>
                </div>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900 uppercase tracking-wide flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-emerald-500" />
            {currentDraftId ? "Listing Düzenle" : "Yeni Listing"}
            {form.tags.some((t) => t.toLowerCase() === "custom") && (
              <span className="text-[10px] font-semibold text-pink-700 bg-pink-100 rounded-full px-2 py-0.5 normal-case" title="Kişiselleştirilebilir ürün — yayın için Kişiselleştirme aktif olmalı">Custom</span>
            )}
          </h1>
          <div className="flex items-center gap-2">
            {saveMsg && <span className={`max-w-md text-xs font-medium ${/hata|gerekli|yayınlanamaz|başarısız|eksik|reddet/i.test(saveMsg) ? "text-red-500" : "text-emerald-600"}`}>{saveMsg}</span>}
            {currentDraftId && (
              <span className={`text-[11px] ${dirty ? "text-amber-600" : "text-gray-400"}`}
                title={dirty ? "Değişiklikler birkaç saniye içinde otomatik kaydedilir" : undefined}>
                {saving ? "Kaydediliyor…" : dirty ? "● Kaydedilmemiş değişiklik" : lastSavedAt ? `✓ Kaydedildi ${lastSavedAt.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}` : ""}
              </span>
            )}
            {!currentDraftId && dirty && <span className="text-[11px] text-amber-600">● Kaydedilmedi — "Kaydet"e basın</span>}
            {currentDraftId && (
              <div className="relative">
                <button onClick={() => (dupOpen ? setDupOpen(false) : void openDuplicate())}
                  title="Bu listeyi başka kargo profilleriyle çoğalt"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                  <Copy className="h-3.5 w-3.5" /> Kopyala
                </button>
                {dupOpen && (
                  <div className="absolute right-0 top-full z-20 mt-1 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                    <p className="mb-2 text-xs text-gray-600">Kargo profili seç — her profil için bir kopya açılır. Başlık aynen kalır.</p>
                    {!shippingProfiles?.length ? <p className="text-xs text-gray-400">Kargo profilleri alınamadı</p> : (
                      <div className="mb-3 flex flex-col gap-1">
                        {shippingProfiles.map((p) => {
                          const id = String(p.shipping_profile_id);
                          const used = dupUsed.includes(id);
                          return (
                            <label key={id} className={`flex items-center gap-2 rounded px-1.5 py-1 text-xs ${used ? "text-gray-400" : "text-gray-700 hover:bg-gray-50"}`}>
                              <input type="checkbox" disabled={used} checked={dupPick.includes(id)}
                                onChange={(e) => setDupPick((cur) => (e.target.checked ? [...cur, id] : cur.filter((x) => x !== id)))} />
                              <span className="flex-1">{p.title}</span>
                              {used && <span className="text-[10px]">{id === form.shippingProfileId ? "bu liste" : "zaten var"}</span>}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {!form.sku.trim() && <p className="mb-2 text-[10px] text-amber-600">SKU yok — aynı profille tekrar liste kontrolü yapılamaz.</p>}
                    <button onClick={() => void handleDuplicate()} disabled={dupBusy || !dupPick.length}
                      className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-50">
                      {dupBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />} {dupPick.length || ""} kopya oluştur
                    </button>
                  </div>
                )}
              </div>
            )}
            {isScheduled && form.scheduledAt && (
              <span className="flex items-center gap-1 text-[10px] font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded px-2 py-1" title="Planlanan yayın tarihi">
                <Calendar className="h-3 w-3" /> Planlandı: {new Date(form.scheduledAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            {!currentEtsyListingId && (
              <button onClick={() => setShowSchedule(!showSchedule)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                <Calendar className="h-3.5 w-3.5" /> {isScheduled ? "Planı Düzenle" : "Planla"}
              </button>
            )}
            {/* Coklu magaza: ayni urunu birden fazla magazaya, her birinin kendi
                stratejisiyle uyarlayarak gonder */}
            {currentDraftId && (
              <button onClick={() => setShowPublishShops(true)}
                title="Bu ürünü seçili mağazalara uyarlayarak gönder"
                className="flex items-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100">
                <Store className="h-3.5 w-3.5" /> Mağazalara Gönder
              </button>
            )}
            <div className="relative">
              <button onClick={() => setShowAiInput((p) => ({ ...p, all: !p.all }))}
                disabled={aiGenerating.all}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50">
                {aiGenerating.all ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} AI Oluştur
              </button>
              {showAiInput.all && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg p-3 w-80">
                  <p className="text-[10px] text-gray-500 mb-2">Başlık + Açıklama + Etiketler için AI ile içerik oluştur</p>
                  {/* Konsept secici */}
                  <label className="text-[10px] font-medium text-gray-500 flex items-center gap-1 mb-1"><Layers className="h-3 w-3" /> Konsept (opsiyonel)</label>
                  <select value={selectedConceptId} onChange={(e) => setSelectedConceptId(e.target.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-amber-200 focus:outline-none mb-1.5">
                    <option value="">Konsept yok</option>
                    {concepts.map((c) => (
                      <option key={c.id} value={c.id}>{c.emoji ? `${c.emoji} ` : ""}{c.name}</option>
                    ))}
                  </select>
                  {selectedConcept && (
                    <div className="mb-2 rounded-lg bg-amber-50 border border-amber-100 p-2">
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {selectedConcept.keywords.slice(0, 6).map((k, i) => (
                          <span key={i} className="rounded bg-white px-1.5 py-0.5 text-[9px] text-amber-700 border border-amber-200">{k}</span>
                        ))}
                      </div>
                      {(selectedConcept.defaultSectionId || selectedConcept.defaultSectionTitle) && (
                        <label className="flex items-center gap-1.5 text-[10px] text-gray-600 cursor-pointer">
                          <input type="checkbox" checked={conceptAutofillSection} onChange={(e) => setConceptAutofillSection(e.target.checked)}
                            className="h-3 w-3 rounded border-gray-300 text-amber-500 focus:ring-amber-400" />
                          Mağaza Bölümü'nü otomatik doldur ({selectedConcept.defaultSectionTitle || selectedConcept.defaultSectionId})
                        </label>
                      )}
                    </div>
                  )}
                  <input type="text" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); generateAiContent("all"); } }}
                    placeholder="Keyword/prompt (boş bırak = mevcut veriden)"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-200 focus:outline-none mb-2" />
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-gray-400">{selectedConcept ? `Konsept: ${selectedConcept.name}` : aiPrompt ? "Hibrit mod" : "Analiz modu"}</span>
                    <button onClick={() => generateAiContent("all")} disabled={aiGenerating.all}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50">
                      {aiGenerating.all ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Oluştur
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => handleSave()} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
            </button>
            <button onClick={handleSaveAsTemplate}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-teal-700 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors">
              <Bookmark className="h-3.5 w-3.5" /> Şablona Kaydet
            </button>
            {/* Tasarimi degistir: her kayitli taslakta. Etsy'deyse fotograflar orada da yenilenir */}
            {currentDraftId && (
              <button onClick={() => setRedesignOpen(true)} disabled={!!redesignBusy || publishing || saving}
                title={currentEtsyListingId
                  ? "Yeni ya da düzeltilmiş tasarımla mockupları yeniden üret ve Etsy'deki fotoğrafları değiştir"
                  : "Yeni ya da düzeltilmiş tasarımla mockupları yeniden üret, taslağın görsellerini değiştir"}
                className="flex items-center gap-1.5 rounded-lg border border-fuchsia-200 bg-fuchsia-50 px-3 py-2 text-xs font-medium text-fuchsia-700 hover:bg-fuchsia-100 disabled:opacity-50">
                {redesignBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} Tasarımı değiştir
              </button>
            )}
            {/* Mockup ekle: kutuphaneden secilen mockup bagli tasarimla basilip eklenir */}
            {currentDraftId && linkedDesigns.length === 1 && (
              <button onClick={() => setAddMockOpen(true)} disabled={addMockBusy || !!redesignBusy || publishing || saving}
                title="Mockup kütüphanesinden seç: listenin tasarımıyla basılıp görsellere eklenir (Mockupları yenile'de de korunur)"
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {addMockBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Mockup ekle
              </button>
            )}
            {refreshOpen && linkedDesigns.length === 1 && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setRefreshOpen(false)}
                onKeyDown={(e) => { if (e.key === "Escape") setRefreshOpen(false); }}>
                <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="refresh-title">
                  <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                    <h3 id="refresh-title" className="flex items-center gap-2 text-base font-semibold text-gray-900"><RefreshCw className="h-4 w-4 text-indigo-600" /> Mockupları yenile</h3>
                    <button onClick={() => setRefreshOpen(false)} aria-label="Kapat" title="Kapat" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><X className="h-4 w-4" /></button>
                  </div>
                  <div className="space-y-4 overflow-y-auto p-5 text-sm text-gray-700">
                    <p>Mockuplar şablonun koleksiyonundan, koleksiyon sırasıyla ve güncel mockup ayarlarıyla (renk etiketi, alan, ışık) yeniden basılır. <b>Ana görsel</b>, satan mockuplar ve "Mockup ekle" ile eklenenler koleksiyonda olmasa da korunur; koleksiyon dışında kalan diğerleri çıkarılır.</p>
                    <div>
                      <label htmlFor="chart-mode" className="mb-1 block text-xs font-medium text-gray-700">Renk tablolarında tasarım</label>
                      <select id="chart-mode" value={chartMode} onChange={(e) => void setChartMode(e.target.value as "auto" | "on" | "off")}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
                        <option value="auto">Otomatik: uygun varyant yoksa düz tablo</option>
                        <option value="on">Açık: alanı çizili her tabloya bas</option>
                        <option value="off">Kapalı: tablolar düz gitsin</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">Otomatik: tasarımın açık/koyu sürümü tablodaki renklerin dörtte birinden fazlasına uymuyorsa (ör. tek sürümlü altın tasarım açık tişörtlerde kaybolur) tablo düz gider. Mockup yönetimindeki "Tasarım bas" anahtarı kapalı tablolar, "Açık" seçilmedikçe düz kalır.</p>
                    </div>
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
                      <span className="text-xs text-gray-600">Koleksiyonda olmayan bir mockup'ı da listeye eklemek için</span>
                      <button onClick={() => setAddMockOpen(true)} disabled={addMockBusy}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        {addMockBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Mockup ekle
                      </button>
                    </div>
                    {/* Onizleme: ne degisecek */}
                    {planBusy && !refreshPlan ? (
                      <p className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="h-4 w-4 animate-spin text-indigo-600" /> Önizleme hazırlanıyor…</p>
                    ) : refreshPlan && (() => {
                      const abs = (u: string) => (u.startsWith("http") ? u : `${API_BASE}${u}`);
                      const Thumb = ({ src, badge, tone, title }: { src: string | null; badge: string; tone: "ok" | "warn" | "bad" | "muted"; title?: string }) => (
                        <figure className="w-20 shrink-0" title={title}>
                          <div className="h-20 w-20 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                            {src ? <img src={abs(src)} alt="" loading="lazy" className="h-full w-full object-cover" /> : <ImageIcon className="m-6 h-8 w-8 text-gray-300" />}
                          </div>
                          <figcaption className={`mt-1 truncate rounded px-1 text-center text-[10px] font-medium ${tone === "ok" ? "bg-emerald-50 text-emerald-700" : tone === "warn" ? "bg-amber-50 text-amber-700" : tone === "bad" ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-600"}`}>{badge}</figcaption>
                        </figure>
                      );
                      const p = refreshPlan;
                      return (
                        <div className={`space-y-3 ${planBusy ? "opacity-60" : ""}`}>
                          <p className="text-xs text-gray-600">
                            <b className="text-gray-900">{p.mockups.length}</b> mockup basılacak · <b className="text-gray-900">{p.dropped.length}</b> çıkarılacak · <b className="text-gray-900">{p.template.length}</b> şablon görseli · toplam ≈ <b className="text-gray-900">{p.total}</b>/20
                          </p>
                          {p.overflow > 0 && <p className="rounded-lg bg-red-50 p-2 text-xs text-red-700">20 görsel sınırı: sondaki ≈{p.overflow} görsel (genelde şablonun bilgi görselleri) düşer. Koleksiyondan ya da elle eklenenlerden azalt.</p>}
                          <div>
                            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Basılacak mockuplar (sırasıyla)</p>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                              {p.mockups.map((m, i) => <Thumb key={m.id} src={m.thumb} title={m.label} tone={m.noArea ? "bad" : m.why === "Koleksiyon" ? "muted" : "ok"} badge={`${i + 1}. ${m.noArea ? "Alan yok" : m.why}`} />)}
                            </div>
                          </div>
                          {p.dropped.length > 0 && (
                            <div>
                              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-red-600">Çıkarılacak (koleksiyonda yok)</p>
                              <div className="flex gap-2 overflow-x-auto pb-1">{p.dropped.map((m) => <Thumb key={m.id} src={m.thumb} title={m.label} tone="bad" badge="Çıkarılacak" />)}</div>
                            </div>
                          )}
                          {p.manual.length > 0 && (
                            <div>
                              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Elle yüklenen, korunacak</p>
                              <div className="flex gap-2 overflow-x-auto pb-1">{p.manual.map((u) => <Thumb key={u} src={u} tone="muted" badge="Korunur" />)}</div>
                            </div>
                          )}
                          {p.template.length > 0 && (
                            <div>
                              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">Şablon görselleri (5. sıradan itibaren)</p>
                              <div className="flex gap-2 overflow-x-auto pb-1">
                                {p.template.map((t) => <Thumb key={t.url} src={t.url} title={t.reason} tone={t.print ? "ok" : t.kind === "chart" ? "warn" : "muted"} badge={t.kind !== "chart" ? "Bilgi" : t.print ? "Tasarım basılır" : "Düz"} />)}
                              </div>
                              {p.template.some((t) => t.kind === "chart" && !t.print) && (
                                <p className="mt-1 text-xs text-gray-500">Düz kalan tablolar: {[...new Set(p.template.filter((t) => t.kind === "chart" && !t.print).map((t) => t.reason))].join(" · ")}</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {currentEtsyListingId && <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-700">Etsy'ye hemen gitmez: yeni görselleri kontrol edip "Etsy'de güncelle"ye basınca gider.</p>}
                  </div>
                  <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
                    <button onClick={() => setRefreshOpen(false)} className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">İptal</button>
                    <button onClick={() => { setRefreshOpen(false); void handleRedesign(linkedDesigns[0].id, true, true); }} disabled={!!redesignBusy || addMockBusy}
                      className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
                      <RefreshCw className="h-4 w-4" /> Yenile
                    </button>
                  </div>
                </div>
              </div>
            )}
            {addMockOpen && (
              <MockupChooserModal
                title="Mockup ekle — tıkladığın mockup listenin tasarımıyla basılıp görsellere eklenir"
                mode="multi" newestFirst
                onPick={(m) => void handleAddMockup(m.id)}
                onClose={() => setAddMockOpen(false)}
              />
            )}
            {/* Mockuplari yenile: ayni tasarim, guncel mockup ayarlari (renk etiketi, alan, isik) */}
            {currentDraftId && linkedDesigns.length === 1 && (
              <button onClick={() => setRefreshOpen(true)} disabled={!!redesignBusy || publishing || saving}
                title={currentEtsyListingId
                  ? "Aynı tasarımla mockupları güncel ayarlarla (renk etiketi vb.) yeniden üret ve Etsy'deki fotoğrafları değiştir"
                  : "Aynı tasarımla mockupları güncel ayarlarla yeniden üret"}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {redesignBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Mockupları yenile
              </button>
            )}
            {currentEtsyListingId ? (
              <>
                <a href={`https://www.etsy.com/listing/${currentEtsyListingId}`} target="_blank" rel="noopener noreferrer"
                  className={`text-[10px] rounded px-2 py-1 font-medium border ${listingActive ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}
                  title="Etsy listesini yeni sekmede aç">
                  {listingActive ? "● Yayında" : "○ Taslak"} · Etsy #{currentEtsyListingId}
                </a>
                <button onClick={() => handlePublishToEtsy(false, false)} disabled={publishing || saving}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors">
                  {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Güncelle
                </button>
                <button onClick={handleResyncListingImages} disabled={resyncingImgs || publishing || saving}
                  title="Etsy'deki görselleri silip taslaktaki tüm görselleri (mockup + tablolar) ve videoyu yeniden gönder"
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50 ${etsyImagesPending ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"}`}>
                  {resyncingImgs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} {etsyImagesPending ? "Etsy'de güncelle" : "Görselleri Yenile"}
                </button>
                {listingActive ? (
                  <span className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <Send className="h-4 w-4" /> Yayında
                  </span>
                ) : (
                  <button onClick={() => handlePublishToEtsy(false, true)} disabled={publishing || saving}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                    {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Yayınla
                  </button>
                )}
              </>
            ) : (
              <>
                <button onClick={() => handlePublishToEtsy(true)} disabled={publishing || saving}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 disabled:opacity-50 transition-colors">
                  {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Taslak Gönder
                </button>
                <button onClick={() => handlePublishToEtsy(false, true)} disabled={publishing || saving}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                  {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Etsy'ye Gönder (Yayınla)
                </button>
              </>
            )}
          </div>
        </div>

        {/* Coklu magaza gonderimi */}
        {showPublishShops && currentDraftId && (
          <PublishToShopsModal
            productId={currentDraftId}
            productTitle={form.title || "İsimsiz ürün"}
            onClose={() => setShowPublishShops(false)}
          />
        )}

        <div className="max-w-4xl mx-auto p-6 flex flex-col gap-6">

          {/* Schedule bar */}
          {showSchedule && (
            <div style={{ order: -3 }} className="p-4 bg-purple-50 border border-purple-200 rounded-xl space-y-3">
              <div className="flex items-center gap-4">
                <Calendar className="h-5 w-5 text-purple-500" />
                <div className="flex-1">
                  <label className="text-xs font-medium text-purple-700 block mb-1">Yayınlama Tarihi</label>
                  <input type="datetime-local" value={form.scheduledAt} onChange={(e) => updateForm({ scheduledAt: e.target.value })}
                    className="text-sm border border-purple-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-purple-200 focus:outline-none" />
                </div>
                <button onClick={async () => {
                    // Kaydet basarisiz olursa (orn. dogrulama hatasi) plan
                    // KURULMUS gibi gosterilmemeli — sayfa yenilenince plan
                    // yokmus gibi gorunuyordu.
                    const ok = await handleSave("scheduled");
                    if (!ok) return;
                    setIsScheduled(true); setShowSchedule(false);
                  }} disabled={!form.scheduledAt || saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">
                  {isScheduled ? "Planı Güncelle" : "Planlı Kaydet"}
                </button>
                {isScheduled && (
                  <button onClick={cancelSchedule} disabled={saving}
                    className="px-3 py-2 text-sm font-medium text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-100 disabled:opacity-50">
                    Planı İptal Et
                  </button>
                )}
              </div>

              {/* Mevcut planli listeler */}
              <div className="border-t border-purple-200 pt-3">
                <p className="text-[11px] font-semibold text-purple-700 mb-1.5">Planlanan Listeler ({scheduledList.length})</p>
                {scheduledList.length === 0 ? (
                  <p className="text-[11px] text-purple-400">Henüz planlanmış liste yok.</p>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {scheduledList.map((s) => (
                      <div key={s.id} className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs ${s.id === currentDraftId ? "bg-purple-100" : "bg-white"} border border-purple-100`}>
                        <Calendar className="h-3 w-3 flex-shrink-0 text-purple-400" />
                        <span className="w-28 flex-shrink-0 font-medium text-purple-700">
                          {s.scheduledAt ? new Date(s.scheduledAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "-"}
                        </span>
                        <span className="flex-1 truncate text-gray-600" title={s.title}>{s.title || "Başlıksız"}</span>
                        {s.id !== currentDraftId && (
                          <button onClick={() => navigate(`/listing-builder?id=${s.id}`)} className="flex-shrink-0 text-[10px] text-indigo-600 hover:underline">Aç</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Template Selector */}
          {(listingTemplates?.length || 0) > 0 && (
            <div style={{ order: -2 }} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <select
                  value={appliedListingTemplateId ?? ""}
                  onChange={(e) => {
                    const tpl = listingTemplates!.find((t) => t.id === e.target.value);
                    if (tpl) { applyListingTemplate(tpl); setAppliedListingTemplateId(tpl.id); }
                    else setAppliedListingTemplateId(null);
                  }}
                  className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                  <option value="">Şablon seç ve uygula ({listingTemplates?.length} şablon)</option>
                  {listingTemplates!.map((t) => {
                    const varCount = t.variations?.reduce((s, v) => s + v.options.length, 0) || 0;
                    const info = [
                      t.tags.length > 0 ? `${t.tags.length} tag` : "",
                      varCount > 0 ? `${varCount} var` : "",
                      t.price ? `$${t.price}` : "",
                    ].filter(Boolean).join(" | ");
                    return (
                      <option key={t.id} value={t.id}>
                        {t.name}{info ? ` — ${info}` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          )}

          {/* Etsy'ye hazir mi — eksikler tiklaninca ilgili bolume gider */}
          <div style={{ order: -2 }} className="space-y-3">
            <ReadinessPanel result={readiness} onGo={goSection} />
            {supplierAttr?.categoryId && (
              <JewelrySourceCard supplier={supplierAttr} pricing={pricingAttr} comboPrices={comboPricesAttr} price={Number(form.price) || 0} />
            )}
            {redesignOpen && (
              <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => !redesignBusy && setRedesignOpen(false)}>
                <div className="my-10 w-full max-w-3xl rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Tasarımı değiştir</h3>
                      <p className="text-sm text-gray-500">
                        Seçtiğin tasarımla mockuplar listenin şablonu ve mockup grubuyla yeniden üretilir
                        {currentEtsyListingId ? ", Etsy'deki fotoğraflar ve video (varsa, yeni mockuplarla yeniden üretilir) değiştirilir" : ""}. {activeShopInfo?.ipStrict && genSeo ? "Başlık ve etiketler yeni tasarıma göre forma yazılır; açıklama, fiyat ve varyasyonlar aynı kalır." : "Başlık, açıklama, fiyat ve varyasyonlar aynı kalır."}
                      </p>
                    </div>
                    <button onClick={() => setRedesignOpen(false)} disabled={!!redesignBusy} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
                  </div>
                  {genAnalysisId && (
                    <div className="mb-4 rounded-xl border border-fuchsia-200 bg-fuchsia-50/40 p-3">
                      <p className="mb-2 text-sm font-semibold text-gray-900">Yeni tasarım üret</p>
                      <p className="mb-2 text-xs text-gray-500">Listenin ilham analizinden ilhamın kopyası olmayan özgün bir tasarım üretilir. Hazır olunca aşağıda çıkar; tıklayınca mockuplar o tasarımla yenilenir.</p>
                      <textarea value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} rows={3} aria-label="Tasarım brief'i"
                        placeholder="Analiz yükleniyor…" className="mb-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-xs" />
                      <div className="flex flex-wrap items-center gap-2">
                        <select value={genFid} onChange={(e) => setGenFid(e.target.value as "balanced" | "loose")} aria-label="İlham seviyesi"
                          className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
                          <option value="balanced">Dengeli</option>
                          <option value="loose">Serbest</option>
                        </select>
                        <select value={genModel} onChange={(e) => setGenModel(e.target.value)} aria-label="Model"
                          className="max-w-[200px] rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
                          {genModels.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                        {activeShopInfo?.ipStrict && (
                          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                            <input type="checkbox" checked={genSeo} onChange={(e) => setGenSeo(e.target.checked)} /> Başlık ve etiketleri de tasarıma göre güncelle
                          </label>
                        )}
                        <button onClick={() => void handleGenerateDesign()} disabled={genBusy || !genPrompt.trim()}
                          className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
                          {genBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Üret
                        </button>
                      </div>
                      {genItems.length > 0 && (
                        <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
                          {genItems.map((g) => (
                            <button key={g.id} onClick={() => g.poolId && void applyGenerated(g.poolId)} disabled={g.status !== "ready" || !!redesignBusy}
                              title={g.error ?? (g.status === "ready" ? "Bu tasarımla değiştir" : "Üretiliyor…")}
                              className="overflow-hidden rounded-xl border border-fuchsia-300 bg-white text-left hover:border-fuchsia-500 disabled:cursor-default">
                              <div className="relative flex aspect-square items-center justify-center bg-[repeating-conic-gradient(#eee_0_25%,#fff_0_50%)] bg-[length:14px_14px]">
                                {g.url && <img src={g.url.startsWith("http") ? g.url : `${API_BASE}${g.url}`} alt="" className="h-full w-full object-contain" />}
                                {g.status === "generating" && <Loader2 className="h-6 w-6 animate-spin text-fuchsia-600" />}
                                {g.status === "failed" && <span className="px-2 text-center text-[11px] text-red-600">{g.error}</span>}
                                {redesignBusy === g.poolId && <span className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 className="h-6 w-6 animate-spin text-fuchsia-600" /></span>}
                              </div>
                              <div className="p-2 text-xs">
                                <p className="font-medium text-fuchsia-700">{g.status === "ready" ? "YENİ · seç" : g.status === "generating" ? "Üretiliyor…" : "Hata"}</p>
                                {g.sku && <p className="text-gray-400">{g.sku}</p>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {linkedDesigns.length > 0 && (
                    <p className="mb-2 text-xs text-gray-500">Mevcut tasarımı düzelttiysen (Tasarımlar'da düzenle) aynı tasarımı seçmen yeterli — güncel hali kullanılır.</p>
                  )}
                  <div className="mb-3 flex items-center gap-2">
                    <input value={redesignQuery} onChange={(e) => setRedesignQuery(e.target.value)} placeholder="Tasarım ara (ad veya SKU)"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
                    <ChatGptFilterButton compact value={redesignChatgpt} onChange={setRedesignChatgpt} />
                  </div>
                  <div className="grid max-h-[55vh] grid-cols-3 gap-3 overflow-y-auto sm:grid-cols-4">
                    {[...linkedDesigns.map((d) => ({ ...d, current: true })), ...redesignList.filter((d) => !linkedDesigns.some((l) => l.id === d.id)).map((d) => ({ ...d, current: false }))].map((d) => {
                      const u = d.editedUrl || d.thumbnailUrl || d.originalUrl;
                      const src = u ? (u.startsWith("http") ? u : `${API_BASE}${u}`) : "";
                      return (
                        <button key={d.id} onClick={() => void applyGenerated(d.id)} disabled={!!redesignBusy}
                          className={`overflow-hidden rounded-xl border text-left hover:border-fuchsia-400 disabled:opacity-50 ${d.current ? "border-indigo-300" : "border-gray-200"}`}>
                          <div className="relative aspect-square bg-[repeating-conic-gradient(#eee_0_25%,#fff_0_50%)] bg-[length:14px_14px]">
                            {src && <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" />}
                            {d.current && <span className="absolute left-1.5 top-1.5 rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">MEVCUT</span>}
                            {redesignBusy === d.id && <span className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 className="h-6 w-6 animate-spin text-fuchsia-600" /></span>}
                          </div>
                          <div className="p-2 text-xs">
                            <p className="truncate font-medium text-gray-800">{d.name}</p>
                            {d.sku && <p className="text-gray-400">{d.sku}</p>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {(linkedDesigns.length > 0 || inspirations.length > 0) && (
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-indigo-200 bg-white p-3 text-xs">
                {linkedDesigns.length > 0 && <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600">Kaynak tasarım</span>}
                {linkedDesigns.map((d) => {
                  const u = d.thumbnailUrl || d.editedUrl || d.originalUrl;
                  const src = u ? (u.startsWith("http") ? u : `${API_BASE}${u}`) : null;
                  return (
                    <a key={d.id} href={`/designs?edit=${d.id}`} target="_blank" rel="noreferrer" title="Tasarımı yeni sekmede düzenle"
                      className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1 hover:border-indigo-300 hover:bg-indigo-50/40">
                      {src && <img src={src} alt="" className="h-10 w-10 rounded object-contain bg-[repeating-conic-gradient(#eee_0_25%,#fff_0_50%)] bg-[length:10px_10px]" />}
                      <span className="max-w-[160px]">
                        <span className="block truncate font-medium text-gray-800">{d.name}</span>
                        {d.sku && <span className="block text-gray-400">{d.sku}</span>}
                      </span>
                    </a>
                  );
                })}
                {/* Ilham alinan analiz + satan listenin fotografi (mockup) */}
                {inspirations.length > 0 && (
                  <span className={`text-[10px] font-semibold uppercase tracking-wide text-amber-600 ${linkedDesigns.length ? "ml-2 border-l border-gray-200 pl-3" : ""}`}>İlham</span>
                )}
                {inspirations.map((a) => {
                  const img = a.source?.imageUrls?.[0];
                  return (
                    <span key={a.id} className="flex items-center gap-2 rounded-lg border border-amber-200 px-2 py-1">
                      {img && (
                        <a href={img} target="_blank" rel="noreferrer" title="Satan listenin fotoğrafı (büyük)">
                          <img src={img} alt="" className="h-10 w-10 rounded object-cover" />
                        </a>
                      )}
                      <span className="max-w-[200px]">
                        <a href={`/analyses/${a.id}`} target="_blank" rel="noreferrer" title="Analizi yeni sekmede aç"
                          className="block truncate font-medium text-gray-800 hover:text-amber-700 hover:underline">
                          {a.source?.title || a.niche || "Analiz"}
                        </a>
                        {a.source?.url && (
                          <a href={a.source.url} target="_blank" rel="noreferrer" className="block text-[10px] text-gray-400 hover:text-amber-700">Etsy'de aç ↗</a>
                        )}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Title (always first) */}
          <div ref={titleRef} style={{ order: -1 }} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Type className="h-4 w-4 text-gray-400" /> Başlık *</label>
              <button onClick={() => setShowAiInput((p) => ({ ...p, title: !p.title }))} disabled={aiGenerating.title}
                className="flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50">
                {aiGenerating.title ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} AI
              </button>
            </div>
            {showAiInput.title && (
              <div className="flex items-center gap-2 mb-2 p-2 bg-amber-50/50 border border-amber-200 rounded-lg">
                <input type="text" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); generateAiContent("title"); } }}
                  placeholder="Keyword (boş = mevcut veriden oluştur)"
                  className="flex-1 text-xs border border-amber-200 rounded px-2 py-1.5 focus:ring-2 focus:ring-amber-200 focus:outline-none" />
                <button onClick={() => generateAiContent("title")} disabled={aiGenerating.title}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50">
                  {aiGenerating.title ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                </button>
              </div>
            )}
            <input type="text" value={form.title} onChange={(e) => updateForm({ title: e.target.value })}
              placeholder="Yeni başlık yazın (min 40 karakter önerilen)" maxLength={140}
              className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
            <p className={`text-[10px] mt-1 ${form.title.length > 140 ? "text-red-500" : "text-gray-400"}`}>{form.title.length}/140</p>
            {originalTitle && (
              <div className="mt-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                <span className="text-[10px] font-semibold text-amber-600 uppercase">Orijinal Başlık:</span>
                <p className="text-xs text-amber-800 mt-0.5 select-all cursor-pointer" title="Tıklayarak kopyalayabilirsiniz"
                  onClick={() => { navigator.clipboard.writeText(originalTitle); setSaveMsg("Orijinal başlık kopyalandı"); setTimeout(() => setSaveMsg(null), 2000); }}>
                  {originalTitle}
                </p>
              </div>
            )}
          </div>

          {/* ===== IMAGES ===== */}
          <div ref={(el) => { sectionRefs.current.images = el; }} style={{ order: sectionCssOrder.images }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><ImageIcon className="h-4 w-4 text-gray-400" /> Görseller</h2>
              <div className="flex items-center gap-2">
                {currentDraftId && (
                  <button
                    onClick={async () => {
                      try {
                        setSaveMsg("Şablon görselleri ekleniyor...");
                        const r = await fetch(`${API_BASE}/api/listing-drafts/${currentDraftId}/add-template-images`, { method: "POST", headers: getAuthHeaders() });
                        const j = await r.json();
                        if (!r.ok) { setSaveMsg(j.error || "Şablon görselleri eklenemedi"); setTimeout(() => setSaveMsg(null), 3500); return; }
                        const urls: string[] = j.data?.imageUrls || [];
                        if (urls.length) updateForm({ imageUrls: urls, imageLabels: urls.map((_, i) => form.imageLabels?.[i] || "") });
                        setSaveMsg(`${j.data?.added || 0} şablon görseli eklendi (toplam ${j.data?.total ?? urls.length})`);
                      } catch { setSaveMsg("Hata oluştu"); }
                      setTimeout(() => setSaveMsg(null), 3500);
                    }}
                    title="Şablonun görsellerini (size chart / marka) bu listeye ekle"
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    <ImageIcon className="h-3 w-3" /> Şablon Görsellerini Ekle
                  </button>
                )}
                {currentEtsyListingId && form.imageUrls.some(u => !u.startsWith("http") && !u.startsWith("data:")) && (
                  <button
                    onClick={async () => {
                      try {
                        setSaveMsg("Etsy'den görseller çekiliyor...");
                        const r = await fetch(`${API_BASE}/api/etsy/listings/${currentEtsyListingId}`, { headers: getAuthHeaders() });
                        if (!r.ok) { setSaveMsg("Etsy görselleri alınamadı"); return; }
                        const j = await r.json();
                        const imgs = j.data?.images || [];
                        if (imgs.length === 0) { setSaveMsg("Etsy'de görsel bulunamadı"); return; }
                        const cdnUrls = imgs
                          .sort((a: { rank: number }, b: { rank: number }) => a.rank - b.rank)
                          .map((img: { url_fullxfull: string }) => img.url_fullxfull)
                          .filter(Boolean);
                        updateForm({ imageUrls: cdnUrls, imageLabels: cdnUrls.map(() => "") });
                        if (currentDraftId) await apiClient.updateListingDraft(currentDraftId, { imageUrls: cdnUrls });
                        setSaveMsg(`${cdnUrls.length} görsel Etsy'den çekildi`);
                      } catch { setSaveMsg("Hata oluştu"); }
                      setTimeout(() => setSaveMsg(null), 3000);
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                  >
                    <RefreshCw className="h-3 w-3" /> Etsy'den Çek
                  </button>
                )}
                <span className="text-[10px] text-gray-400">{form.imageUrls.length}/20</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {form.imageUrls.map((url, i) => {
                const label = form.imageLabels[i] || "";
                const full = url.startsWith("http") || url.startsWith("data:") ? url : `${API_BASE}${url}`;
                return (
                  <div key={url}
                    draggable
                    onDragStart={() => setDragImgIdx(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragImgIdx !== null) reorderImages(dragImgIdx, i); setDragImgIdx(null); }}
                    onDragEnd={() => setDragImgIdx(null)}
                    className={`relative group rounded-lg border border-gray-200 overflow-hidden bg-gray-50 cursor-grab active:cursor-grabbing ${dragImgIdx === i ? "opacity-40" : ""}`}>
                    <img src={full} alt="" className="w-full h-40 object-contain cursor-zoom-in" onClick={() => setImgLightbox(i)} />
                    <button onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 p-1 rounded bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-3 w-3" />
                    </button>
                    <select value={label} onChange={(e) => setImageLabel(i, e.target.value)}
                      className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] py-0.5 px-1 border-0 outline-none cursor-pointer appearance-none text-center">
                      <option value="">#{i + 1}</option>
                      <option value="size-color-chart">Size-Color Chart</option>
                      <option value="mockup">Mockup</option>
                    </select>
                  </div>
                );
              })}
              {form.imageUrls.length < 20 && (
                <button onClick={() => imageFileRef.current?.click()}
                  className="rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1 h-40 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors">
                  <Upload className="h-5 w-5 text-gray-300" />
                  <span className="text-[10px] text-gray-400">Yükle</span>
                </button>
              )}
            </div>
            <input ref={imageFileRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />

          </div>

          {/* ===== VIDEO ===== */}
          <div ref={(el) => { sectionRefs.current.video = el; }} style={{ order: sectionCssOrder.video }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Video className="h-4 w-4 text-gray-400" /> Video</h2>
              <div className="flex items-center gap-3">
                <button onClick={() => void handleGenerateVideo()} disabled={videoBusy || form.imageUrls.length === 0}
                  title="Ürün fotoğraflarından (ilk 6) 8 saniyelik kare video — kaydedince taslağa yazılır, Etsy'ye gönderince yüklenir"
                  className="flex items-center gap-1 rounded-full bg-[#222] px-3 py-1 text-xs font-bold text-white hover:bg-black disabled:opacity-50">
                  {videoBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  {form.videoUrl ? "Yeniden oluştur" : "Fotoğraflardan video oluştur"}
                </button>
                {form.videoUrl && currentEtsyListingId && (
                  <button onClick={() => void handleResyncVideo()} disabled={videoBusy}
                    title="Yalnız videoyu Etsy'de değiştirir — görsellere dokunmaz"
                    className="flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    <Upload className="h-3 w-3" /> Etsy'de güncelle
                  </button>
                )}
                {form.videoUrl && (
                  <button onClick={() => { videoRemovedRef.current = true; updateForm({ videoUrl: "" }); }} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"><X className="h-3 w-3" /> Kaldır</button>
                )}
              </div>
            </div>
            {form.videoUrl ? (
              <div className="relative rounded-lg border border-gray-200 overflow-hidden bg-gray-50 max-w-xs">
                {/* Goreli adres (/api/files/...) API adresiyle tamamlanir */}
                <video src={/^(https?:|blob:|data:)/.test(form.videoUrl) ? form.videoUrl : `${(import.meta.env.VITE_API_URL || "").replace(/\/$/, "")}${form.videoUrl}`}
                  className="w-full h-48 object-contain bg-black" controls />
              </div>
            ) : (
              <button onClick={() => videoFileRef.current?.click()}
                className="w-72 h-52 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-3 hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors cursor-pointer">
                <div className="w-12 h-12 rounded-full bg-indigo-500 flex items-center justify-center"><Plus className="h-6 w-6 text-white" /></div>
                <span className="text-xs text-gray-400">Video yükle — en fazla 100 MB</span>
              </button>
            )}
            <input ref={videoFileRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
          </div>

          {/* ===== DESCRIPTION ===== */}
          <div ref={(el) => { sectionRefs.current.description = el; }} style={{ order: sectionCssOrder.description }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><AlignLeft className="h-4 w-4 text-gray-400" /> Açıklama</h2>
              <button onClick={() => setShowAiInput((p) => ({ ...p, description: !p.description }))} disabled={aiGenerating.description}
                className="flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50">
                {aiGenerating.description ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} AI
              </button>
            </div>
            {showAiInput.description && (
              <div className="flex items-center gap-2 mb-3 p-2 bg-amber-50/50 border border-amber-200 rounded-lg">
                <input type="text" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); generateAiContent("description"); } }}
                  placeholder="Keyword (boş = mevcut veriden oluştur)"
                  className="flex-1 text-xs border border-amber-200 rounded px-2 py-1.5 focus:ring-2 focus:ring-amber-200 focus:outline-none" />
                <button onClick={() => generateAiContent("description")} disabled={aiGenerating.description}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50">
                  {aiGenerating.description ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                </button>
              </div>
            )}
            <textarea value={form.description} onChange={(e) => updateForm({ description: e.target.value })} rows={8}
              placeholder={"ÜRÜN HAKKINDA\n[Ürün açıklaması]\n\nİÇERİK\n- [Özellik 1]\n- [Özellik 2]\n\nKARGO\n[Kargo bilgisi]"}
              className="w-full text-sm border border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-200 focus:outline-none resize-y" />
          </div>

          {/* ===== TAGS ===== */}
          <div ref={(el) => { sectionRefs.current.tags = el; }} style={{ order: sectionCssOrder.tags }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Tag className="h-4 w-4 text-gray-400" /> Etiketler</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowAiInput((p) => ({ ...p, tags: !p.tags }))} disabled={aiGenerating.tags}
                  className="flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50">
                  {aiGenerating.tags ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} AI
                </button>
                <span className={`text-[10px] ${form.tags.length >= 13 ? "text-amber-500" : "text-gray-400"}`}>{form.tags.length}/13</span>
              </div>
            </div>
            {showAiInput.tags && (
              <div className="flex items-center gap-2 mb-3 p-2 bg-amber-50/50 border border-amber-200 rounded-lg">
                <input type="text" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); generateAiContent("tags"); } }}
                  placeholder="Keyword (boş = mevcut veriden oluştur)"
                  className="flex-1 text-xs border border-amber-200 rounded px-2 py-1.5 focus:ring-2 focus:ring-amber-200 focus:outline-none" />
                <button onClick={() => generateAiContent("tags")} disabled={aiGenerating.tags}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50">
                  {aiGenerating.tags ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 min-h-[28px] mb-3">
              {form.tags.map((t, i) => (
                <span key={i} className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${t.length > 20 ? "bg-red-50 text-red-700 border-red-300" : "bg-indigo-50 text-indigo-700 border-indigo-200"}`} title={t.length > 20 ? `${t.length} karakter (max 20)` : ""}>
                  {t} {t.length > 20 && <span className="text-[9px] text-red-500">({t.length})</span>}
                  <button onClick={() => updateForm({ tags: form.tags.filter((_, idx) => idx !== i) })} className="hover:text-red-500"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Etiket yaz ve Enter" maxLength={20}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
              <button onClick={addTag} disabled={form.tags.length >= 13}
                className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 disabled:opacity-50">Ekle</button>
            </div>
          </div>

          {/* ===== PRICING ===== */}
          <div ref={(el) => { sectionRefs.current.pricing = el; }} style={{ order: sectionCssOrder.pricing }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3"><DollarSign className="h-4 w-4 text-gray-400" /> Fiyat & Envanter</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Fiyat (USD) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input type="number" value={form.price} onChange={(e) => updateForm({ price: e.target.value })}
                      placeholder="19.99" step="0.01" min="0"
                      className="w-full text-sm border border-gray-200 rounded-lg pl-7 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Miktar *</label>
                  <input type="number" value={form.quantity} onChange={(e) => updateForm({ quantity: e.target.value })}
                    min="1" className="w-full text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">SKU</label>
                  <input type="text" value={form.sku} onChange={(e) => updateForm({ sku: e.target.value })}
                    placeholder="TSH-BLK-001" maxLength={32}
                    className="w-full text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                </div>
              </div>
            </div>
          </div>

          {/* ===== INVENTORY ===== */}
          <div ref={(el) => { sectionRefs.current.inventory = el; }} style={{ order: sectionCssOrder.inventory }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3"><Package className="h-4 w-4 text-gray-400" /> Envanter</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Kim Yaptı</label>
                <select value={form.whoMade} onChange={(e) => updateForm({ whoMade: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                  <option value="i_did">I did</option>
                  <option value="someone_else">A member of my shop</option>
                  <option value="collective">Another company or person</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Nedir?</label>
                <select value={form.whatIsIt} onChange={(e) => updateForm({ whatIsIt: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                  <option value="finished_product">A finished product</option>
                  <option value="supply_or_tool">A supply or tool to make things</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Ne Zaman Yapıldı?</label>
                <select value={form.whenMade} onChange={(e) => updateForm({ whenMade: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                  <option value="made_to_order">Made to Order</option>
                  <option value="2020_2025">2020-2025</option>
                  <option value="2010_2019">2010-2019</option>
                  <option value="before_2010">2010 öncesi</option>
                </select>
              </div>
            </div>
          </div>

          {/* ===== DETAILS ===== */}
          <div ref={(el) => { sectionRefs.current.details = el; }} style={{ order: sectionCssOrder.details }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-4"><Settings2 className="h-4 w-4 text-gray-400" /> Detaylar</h2>
            <div className="space-y-4">
              {/* Category - Etsy Taxonomy */}
              <div>
                <label className="text-xs text-gray-500 block mb-1">Kategori (Etsy Taxonomy) *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={taxonomySearch || (form.taxonomyId ? (flatTaxonomy.find(t => t.id === Number(form.taxonomyId))?.path || `ID: ${form.taxonomyId}`) : "")}
                    onChange={(e) => {
                      setTaxonomySearch(e.target.value);
                      if (!e.target.value) updateForm({ taxonomyId: "", category: "" });
                    }}
                    onFocus={() => { if (!taxonomySearch && form.taxonomyId) setTaxonomySearch(flatTaxonomy.find(t => t.id === Number(form.taxonomyId))?.path || ""); }}
                    placeholder="Kategori ara... (örn: T-Shirt, Mug, Hoodie)"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none"
                  />
                  {taxonomySearch && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {flatTaxonomy
                        .filter(t => t.path.toLowerCase().includes(taxonomySearch.toLowerCase()))
                        .slice(0, 30)
                        .map(t => (
                          <button
                            key={t.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b border-gray-50 last:border-0"
                            onClick={() => {
                              updateForm({ taxonomyId: String(t.id), category: t.path });
                              setTaxonomySearch("");
                            }}
                          >
                            <span className="text-gray-700">{t.path}</span>
                            <span className="text-xs text-gray-400 ml-2">#{t.id}</span>
                          </button>
                        ))}
                      {flatTaxonomy.filter(t => t.path.toLowerCase().includes(taxonomySearch.toLowerCase())).length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-400">Sonuç bulunamadı</div>
                      )}
                    </div>
                  )}
                </div>
                {form.taxonomyId && <p className="text-[10px] text-gray-400 mt-1">Taxonomy ID: {form.taxonomyId}</p>}
              </div>

              {/* Type (yenileme Ayarlar bolumunde) */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-xs text-gray-500 block mb-2">Tip</label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={!form.isDigital} onChange={() => updateForm({ isDigital: false, listingType: "physical" })} className="text-indigo-600 focus:ring-indigo-200" />
                      <span className="text-sm text-gray-700">Fiziksel</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={form.isDigital} onChange={() => updateForm({ isDigital: true, listingType: "digital" })} className="text-indigo-600 focus:ring-indigo-200" />
                      <span className="text-sm text-gray-700">Dijital</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Production Partner */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-gray-500">Ürün Ortağı</label>
                    <button onClick={() => refetchPartners()} className="text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5">
                      <RefreshCw className={`h-2.5 w-2.5 ${partnersLoading ? "animate-spin" : ""}`} /> Yenile
                    </button>
                  </div>
                  {partnersLoading ? (
                    <div className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 text-gray-400 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Yükleniyor...</div>
                  ) : productionPartners && productionPartners.length > 0 ? (
                    <select value={form.productionPartnerId} onChange={(e) => updateForm({ productionPartnerId: e.target.value })}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                      <option value="">Hiçbiri</option>
                      {productionPartners.map((p) => (
                        <option key={p.production_partner_id} value={String(p.production_partner_id)}>
                          {p.partner_name}{p.location ? ` - ${p.location}` : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={form.productionPartnerId} onChange={(e) => updateForm({ productionPartnerId: e.target.value })}
                      placeholder="Ürün ortağı ID" className="w-full text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                  )}
                </div>

              </div>

              {/* Materials */}
              <div>
                <label className="text-xs text-gray-500 block mb-1">Malzemeler</label>
                <div className="flex flex-wrap gap-1.5 min-h-[28px] mb-2">
                  {form.materials.map((m, i) => (
                    <span key={i} className="flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full">
                      {m}
                      <button onClick={() => updateForm({ materials: form.materials.filter((_, idx) => idx !== i) })} className="hover:text-red-500"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input type="text" value={materialInput} onChange={(e) => setMaterialInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMaterial(); } }}
                    placeholder="Malzeme ekle..." className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                </div>
              </div>

              {/* Etsy kategori ozellikleri — kategoriye gore Etsy'den; yayinda Etsy'ye yazilir */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <EtsyPropertiesEditor
                  taxProps={taxPropsQ.data} loading={taxPropsQ.isLoading && !!form.taxonomyId}
                  error={taxPropsQ.error instanceof Error ? taxPropsQ.error.message : null}
                  value={etsyProps} onChange={setEtsyProps}
                  variationNames={form.variations.map((v) => v.name)}
                  styles={etsyStyles} onStylesChange={setEtsyStyles}
                  onAutoFill={() => void autoFillEtsyProps()} autoFilling={propsFilling}
                />
                {propsError && <p className="mt-1 text-[11px] text-red-600">{propsError}</p>}
              </div>
            </div>
          </div>

          {/* ===== VARIATIONS ===== */}
          <div ref={(el) => { sectionRefs.current.variations = el; }} style={{ order: sectionCssOrder.variations }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Layers className="h-4 w-4 text-gray-400" /> Varyasyonlar
                {totalVariationOptions > 0 && <span className="text-xs text-gray-400">({totalVariationOptions} seçenek)</span>}
                {(() => {
                  const combos = form.variations.length > 0 ? form.variations.reduce((n, v) => n * v.options.length, 1) : 0;
                  if (combos === 0) return null;
                  return combos > MAX_TOTAL_COMBOS
                    ? <span className="text-xs font-semibold text-red-600">· {combos} kombinasyon — bu fiyat yapısında Etsy sınırı {MAX_TOTAL_COMBOS}, yüklenemez</span>
                    : <span className="text-xs text-gray-400">· {combos}/{MAX_TOTAL_COMBOS} kombinasyon</span>;
                })()}
              </h2>
              {form.variations.length < MAX_VARIATION_TYPES && (
                <button onClick={addVariation}
                  className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium bg-indigo-50 px-3 py-1.5 rounded-lg hover:bg-indigo-100 transition-colors">
                  <Plus className="h-3.5 w-3.5" /> Varyasyon Ekle
                </button>
              )}
            </div>

            <datalist id="erc-variation-names">
              {VARIATION_NAME_SUGGESTIONS.map((n) => <option key={n} value={n} />)}
            </datalist>
            {/* Saved variation templates */}
            {varTemplates && varTemplates.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="text-[10px] text-gray-400 font-medium self-center">Kayıtlı Şablonlar:</span>
                {varTemplates.map((t) => (
                  <button key={t.id} onClick={() => applyVarTemplate(t)}
                    className={`text-[11px] px-3 py-1.5 rounded-lg border transition-colors ${
                      t.category === "combo-catalog"
                        ? "border-purple-200 hover:border-purple-400 hover:bg-purple-50 text-purple-600"
                        : "border-green-200 hover:border-green-400 hover:bg-green-50 text-green-600"
                    }`}>
                    <Layers className="h-3 w-3 inline mr-1" />{t.name}
                  </button>
                ))}
              </div>
            )}

            {form.variations.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Henüz varyasyon eklenmedi</p>
                <p className="text-[10px] mt-1">Kayıtlı şablonlardan seçin veya "Varyasyon Ekle" butonunu kullanın</p>
              </div>
            ) : (
              <>
                {/* Varyasyon tanimlari - compact */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                  {form.variations.map((v, vi) => (
                    <div key={vi} className="border border-gray-200 rounded-xl bg-gray-50 overflow-hidden">
                      <div className="flex items-center justify-between bg-white px-3 py-2 border-b border-gray-200">
                        {/* Serbest ad: takida "Number of Hearts", "Finish" gibi eksenler var */}
                        <input value={v.name} onChange={(e) => updateVariationName(vi, e.target.value)} list="erc-variation-names"
                          placeholder="Eksen adı (ör. Size, Color, Number of Hearts)" title="Etsy'de müşterinin gördüğü varyasyon adı"
                          className="w-44 text-xs font-medium border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                        <div className="flex items-center gap-1">
                          <span className={`text-[10px] ${v.options.length >= MAX_OPTIONS_PER_VARIATION ? "text-red-500 font-medium" : "text-gray-400"}`}>{v.options.length}/{MAX_OPTIONS_PER_VARIATION} seçenek</span>
                          <button onClick={() => removeVariation(vi)} className="p-1 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                      <div className="p-2 space-y-1">
                        {v.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-1.5 bg-white rounded-lg px-2 py-1.5 border border-gray-200">
                            <input type="text" value={opt.label} onChange={(e) => updateOptionLabel(vi, oi, e.target.value)}
                              placeholder="Seçenek adı" className="flex-1 text-xs border-0 focus:ring-0 focus:outline-none bg-transparent p-0" />
                            <span className="text-[10px] text-gray-400">$</span>
                            <input type="number" min="0" step="0.01"
                              value={(opt as VariationOption & { sellingPrice?: number }).sellingPrice ?? ""}
                              onChange={(e) => updateOptionSellingPrice(vi, oi, e.target.value)}
                              placeholder={`${comboSellingPrice([opt], Number(form.price) || 0).toFixed(2)}`}
                              title="Bu seçeneğin sabit satış fiyatı (boş: taban fiyat + ek fiyat)"
                              className="w-16 text-[11px] text-right border border-gray-200 rounded px-1 py-0.5 focus:ring-1 focus:ring-indigo-200 focus:outline-none" />
                            <button onClick={() => removeOption(vi, oi)} className="p-0.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                          </div>
                        ))}
                        <div className="flex items-center gap-1.5 mt-1">
                          <input type="text" value={newOptionInputs[vi] || ""} onChange={(e) => setNewOptionInputs({ ...newOptionInputs, [vi]: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(vi); } }}
                            placeholder="Seçenek ekle" className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                          <button onClick={() => addOption(vi)} className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-500 rounded-lg hover:bg-indigo-600">Ekle</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {form.variations.length > 2 && (
                  <p className="mb-3 rounded-lg bg-indigo-50 px-3 py-2 text-[11px] text-indigo-700">
                    3 eksenli listede fiyat tablosu gösterilmez — fiyatı seçenek satırlarındaki $ kutusundan verin (fiyat tek eksende olursa Etsy 2500 kombinasyona izin verir).
                  </p>
                )}
                {/* Etsy Combo Grid - Size x Color fiyat tablosu */}
                {variationCombos && variationCombos.count > 0 && form.variations.length === 2 && (
                  <div className="border border-indigo-200 rounded-xl overflow-hidden">
                    <div className="bg-indigo-50 px-4 py-2 border-b border-indigo-200 flex items-center justify-between">
                      <h3 className="text-xs font-semibold text-indigo-700">
                        Etsy Varyasyon Tablosu — {variationCombos.v1Name} × {variationCombos.v2Name} ({variationCombos.count} kombinasyon)
                        {form.disabledCombos.length > 0 && <span className="ml-2 text-amber-600 font-medium">· {form.disabledCombos.length} devre dışı</span>}
                        {variationCombos.count > MAX_TOTAL_COMBOS && <span className="ml-2 text-red-600 font-bold">⚠ Max {MAX_TOTAL_COMBOS}</span>}
                      </h3>
                      <span className="text-[10px] text-indigo-500">
                        Baz fiyat: ${Number(form.price || 0).toFixed(2)}
                        {(comboPricesAttr?.length ?? 0) > 0 && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">sarı: en ucuz varyasyon</span>}
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left px-3 py-2 font-semibold text-gray-600 w-48">{variationCombos.v1Name} / {variationCombos.v2Name}</th>
                            {form.variations[1].options.map((opt2, j) => (
                              <th key={j} className="text-center px-2 py-2 font-medium text-gray-600 min-w-[90px]">{opt2.label || `${variationCombos.v2Name} ${j + 1}`}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {form.variations[0].options.map((opt1, i) => (
                            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium text-gray-700 bg-gray-50">{opt1.label || `${variationCombos.v1Name} ${i + 1}`}</td>
                              {form.variations[1].options.map((opt2, j) => {
                                const basePrice = Number(form.price) || 0;
                                const cellLabels = { [form.variations[0].name]: opt1.label, [form.variations[1].name]: opt2.label };
                                const override = comboOverrideFor(cellLabels);
                                const total = override?.price ?? comboSellingPrice([opt1, opt2], basePrice);
                                const cellDisabled = (form.disabledCombos ?? []).some(
                                  (d) => d[form.variations[0].name] === opt1.label && d[form.variations[1].name] === opt2.label
                                );
                                return (
                                  <td key={j} className="px-2 py-1.5 text-center">
                                    {cellDisabled ? (
                                      <span title="Bu kombinasyon devre dışı — kategoride bu renk yok (Etsy'de is_enabled=false)"
                                        className="inline-flex items-center justify-center w-20 text-[11px] text-gray-300 line-through bg-gray-50 border border-dashed border-gray-200 rounded px-1.5 py-1">—</span>
                                    ) : override ? (
                                      <input type="number" value={total.toFixed(2)}
                                        title="En ucuz varyasyon — bu kombinasyon Etsy'ye bu özel fiyatla gider"
                                        onChange={(e) => setComboOverride(cellLabels, Number(e.target.value))}
                                        className="w-20 text-[11px] font-semibold text-amber-800 border border-amber-300 rounded px-1.5 py-1 text-center focus:ring-2 focus:ring-amber-200 focus:outline-none bg-amber-50" step="0.01" />
                                    ) : (
                                      <input type="number" value={total.toFixed(2)}
                                        onChange={(e) => updateComboPrice(i, j, Number(e.target.value))}
                                        className="w-20 text-[11px] font-medium text-emerald-700 border border-gray-200 rounded px-1.5 py-1 text-center focus:ring-2 focus:ring-indigo-200 focus:outline-none bg-emerald-50" step="0.01" />
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Tek varyasyonlu liste */}
                {form.variations.length === 1 && (
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <h3 className="text-xs font-semibold text-gray-600">
                        {form.variations[0].name} Fiyat Listesi ({form.variations[0].options.length} seçenek)
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {form.variations[0].options.map((opt, oi) => {
                        const basePrice = Number(form.price) || 0;
                        const override = comboOverrideFor({ [form.variations[0].name]: opt.label });
                        const total = override?.price ?? comboSellingPrice([opt], basePrice);
                        return (
                          <div key={oi} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50">
                            <span className="text-xs font-medium text-gray-700">{opt.label}</span>
                            <span className={`text-xs font-semibold ${override ? "text-amber-700" : "text-emerald-600"}`}
                              title={override ? "En ucuz varyasyon — Etsy'ye bu özel fiyatla gider" : undefined}>${total.toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ===== PERSONALIZATION ===== */}
          <div ref={(el) => { sectionRefs.current.personalization = el; }} style={{ order: sectionCssOrder.personalization }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Pencil className="h-4 w-4 text-gray-400" /> Kişiselleştirme</h2>
              {currentEtsyListingId && (
                <button
                  onClick={async () => {
                    try {
                      setSaveMsg("Etsy'den kişiselleştirme çekiliyor...");
                      const r = await fetch(`${API_BASE}/api/etsy/listings/${currentEtsyListingId}`, { headers: getAuthHeaders() });
                      if (!r.ok) { setSaveMsg("Etsy kişiselleştirme alınamadı"); return; }
                      const j = await r.json();
                      const questions = parseEtsyPersonalizationQuestions(j.data?.personalization);
                      if (questions.length === 0) { setSaveMsg("Etsy'de kişiselleştirme bulunamadı"); return; }
                      updateForm({ personalization: { enabled: true, questions } });
                      setSaveMsg(`${questions.length} soru Etsy'den çekildi`);
                    } catch { setSaveMsg("Hata oluştu"); }
                    setTimeout(() => setSaveMsg(null), 3000);
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                >
                  <RefreshCw className="h-3 w-3" /> Etsy'den Çek
                </button>
              )}
            </div>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={form.personalization.enabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  const questions = enabled && form.personalization.questions.length === 0
                    ? [{ question_type: "text_input" as const, question_text: "", required: true, max_allowed_characters: 50 }]
                    : form.personalization.questions;
                  updateForm({ personalization: { enabled, questions } });
                }}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-200" />
              <span className="text-sm text-gray-700">Kişiselleştirme aktif</span>
            </label>
            {form.personalization.enabled && (
              <>
                <p className="text-[11px] text-gray-400 mb-3">Etsy en fazla 5 soru destekler: metin, açılır liste ve dosya yükleme. Liste başına en fazla 1 dosya yükleme sorusu.</p>
                <PersonalizationEditor
                  accent="indigo"
                  questions={form.personalization.questions}
                  onChange={(questions) => updateForm({ personalization: { ...form.personalization, questions } })}
                />
              </>
            )}
          </div>

          {/* ===== ETSY ONIZLEMESI (kisisellestirmenin ardinda; ayni order, DOM sirasi) ===== */}
          <div style={{ order: sectionCssOrder.personalization ?? 0 }}>
            <EtsyListingPreviewBox title={form.title} images={form.imageUrls} price={Number(form.price) || null}
              pricesAreListPrices variations={form.variations} personalization={form.personalization.enabled ? form.personalization : null} />
          </div>

          {/* ===== SHIPPING ===== */}
          <div ref={(el) => { sectionRefs.current.shipping = el; }} style={{ order: sectionCssOrder.shipping }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Truck className="h-4 w-4 text-gray-400" /> İşleme & Gönderim</h2>
              <button onClick={() => refetchProfiles()} className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                <RefreshCw className="h-3 w-3" /> Profilleri Yenile
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mb-4">Kargo profili ve sipariş işleme süresini belirleyin.</p>
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">İşleme Profili</label>
                  <button onClick={() => refetchReadinessStates()} className="flex items-center gap-1 text-[10px] text-indigo-600 hover:text-indigo-700 font-medium">
                    <RefreshCw className="h-3 w-3" /> Yenile
                  </button>
                </div>
                {readinessStates && readinessStates.length > 0 ? (
                  <select value={form.processingProfileId} onChange={(e) => updateForm({ processingProfileId: e.target.value })}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                    <option value="">Profil seç...</option>
                    {readinessStates.map((rs) => (
                      <option key={rs.readiness_state_id} value={String(rs.readiness_state_id)}>
                        {rs.readiness_state_label || rs.readiness_state}
                        {rs.min_processing_days != null && rs.max_processing_days != null
                          ? ` (${rs.min_processing_days}-${rs.max_processing_days} gün)`
                          : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select value={form.processingProfileId} onChange={(e) => updateForm({ processingProfileId: e.target.value })}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                    <option value="">Profil seç...</option>
                    <option value="made_to_order_1_2">Made to order - 1-2 days</option>
                    <option value="made_to_order_1_3">Made to order - 1-3 days</option>
                    <option value="made_to_order_3_5">Made to order - 3-5 days</option>
                    <option value="made_to_order_1_7">Made to order - 1-7 days</option>
                    <option value="ready_to_ship_1_2">Ready to ship - 1-2 days</option>
                    <option value="ready_to_ship_1_3">Ready to ship - 1-3 days</option>
                  </select>
                )}
                <p className="text-[10px] text-gray-400 mt-1">Etsy'de tanımlı işleme profilleriniz listelenir.</p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="text-xs font-medium text-gray-700 block mb-2">Kargo Seçeneği</label>
                {shippingProfiles && shippingProfiles.length > 0 ? (
                  <select value={form.shippingProfileId} onChange={(e) => {
                    updateForm({ shippingProfileId: e.target.value });
                    // Son secim hatirlanir (yeni taslaklarda varsayilan)
                    try { if (e.target.value) localStorage.setItem("erc:last-shipping-profile", e.target.value); } catch { /* */ }
                  }}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                    <option value="">Profil seç</option>
                    {shippingProfiles.map((p) => (
                      <option key={p.shipping_profile_id} value={String(p.shipping_profile_id)}>{p.title}</option>
                    ))}
                  </select>
                ) : (
                  <div>
                    <input type="text" value={form.shippingProfileId} onChange={(e) => updateForm({ shippingProfileId: e.target.value })}
                      placeholder="Etsy kargo profili ID'si"
                      className="w-full text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                    <p className="text-[10px] text-gray-400 mt-1">Etsy bağlantısı yapıldığında kargo profilleri otomatik listelenecek.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ===== SETTINGS ===== */}
          <div ref={(el) => { sectionRefs.current.settings = el; }} style={{ order: sectionCssOrder.settings }} className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-16">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Settings className="h-4 w-4 text-gray-400" /> Ayarlar</h2>
              <button onClick={() => refetchSections()} className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                <RefreshCw className="h-3 w-3" /> Yenile
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mb-4">Mağaza görüntüleme ve yenileme ayarları.</p>
            <div className="space-y-5">
              {/* Returns */}
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">İade ve Değişim *</label>
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        {form.returnPolicyId && form.returnPolicyId !== "no_returns"
                          ? `Politika ID: ${form.returnPolicyId}`
                          : "İade politikası seçilmedi"}
                      </p>
                      <p className="text-xs text-gray-400">Etsy'de tanımlı iade politikalarınız listelenir. Fiziksel ürünler için zorunludur.</p>
                    </div>
                    <select value={form.returnPolicyId || ""} onChange={(e) => updateForm({ returnPolicyId: e.target.value })}
                      className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                      <option value="">Seç...</option>
                      {returnPolicies && returnPolicies.length > 0 ? (
                        returnPolicies.map((rp) => (
                          <option key={rp.return_policy_id} value={String(rp.return_policy_id)}>
                            {rp.accepts_returns && rp.accepts_exchanges ? "İade ve değişim kabul edilir"
                              : rp.accepts_returns ? "İade kabul edilir"
                              : rp.accepts_exchanges ? "Değişim kabul edilir"
                              : "İade/değişim yok"} (#{rp.return_policy_id})
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>Etsy'de iade politikası bulunamadı</option>
                      )}
                    </select>
                  </div>
                </div>
              </div>
              {/* Shop Section */}
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Mağaza Bölümü</label>
                {shopSections && shopSections.length > 0 ? (
                  <select value={form.sectionId} onChange={(e) => updateForm({ sectionId: e.target.value })}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                    <option value="">Hiçbiri</option>
                    {shopSections.map((s) => (
                      <option key={s.shop_section_id} value={String(s.shop_section_id)}>{s.title} ({s.active_listing_count} ürün)</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={form.sectionId} onChange={(e) => updateForm({ sectionId: e.target.value })}
                    placeholder="Mağaza bölümü ID'si" className="w-full text-sm border border-gray-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                )}
              </div>
              {/* Featured */}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-xs font-medium text-gray-700 block">Bu Listingi Öne Çıkar</label>
                    <p className="text-[10px] text-gray-400">Mağazanızın ana sayfasında öne çıkararak dikkat çekici hale getirin.</p>
                  </div>
                  <button onClick={() => updateForm({ isFeatured: !form.isFeatured })}
                    className={`relative w-11 h-6 rounded-full transition-colors ${form.isFeatured ? "bg-indigo-500" : "bg-gray-300"}`}>
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.isFeatured ? "translate-x-5" : ""}`} />
                  </button>
                </div>
              </div>
              {/* Renewal */}
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Yenileme Seçenekleri *</label>
                <p className="text-[10px] text-gray-400 mb-2">Her yenileme dört ay sürer veya listing tükenene kadar geçerlidir.</p>
                <div className="space-y-2">
                  <label className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${form.autoRenew ? "border-indigo-400 bg-indigo-50/50" : "border-gray-200 hover:bg-gray-50"}`}>
                    <input type="radio" name="renewalOption" checked={form.autoRenew} onChange={() => updateForm({ autoRenew: true })} className="mt-0.5 text-indigo-600 focus:ring-indigo-200" />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Otomatik</span>
                      <p className="text-xs text-gray-400">Bu listing süresi dolduğunda her seferinde 0,20 USD karşılığında yenilenir (önerilen).</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${!form.autoRenew ? "border-indigo-400 bg-indigo-50/50" : "border-gray-200 hover:bg-gray-50"}`}>
                    <input type="radio" name="renewalOption" checked={!form.autoRenew} onChange={() => updateForm({ autoRenew: false })} className="mt-0.5 text-indigo-600 focus:ring-indigo-200" />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Manuel</span>
                      <p className="text-xs text-gray-400">Süresi dolan listingleri kendim yenileyeceğim.</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Fullscreen image lightbox with prev/next navigation */}
      {imgLightbox !== null && form.imageUrls[imgLightbox] && (() => {
        const u = form.imageUrls[imgLightbox];
        const full = u.startsWith("http") || u.startsWith("data:") ? u : `${API_BASE}${u}`;
        return (
          <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-8" onClick={() => setImgLightbox(null)}>
            <button onClick={() => setImgLightbox(null)} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 text-white z-10">
              <X className="h-6 w-6" />
            </button>
            {form.imageUrls.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); setImgLightbox((i) => (i === null ? i : (i - 1 + form.imageUrls.length) % form.imageUrls.length)); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full hover:bg-white/20 text-white z-10" title="Önceki (←)">
                  <ChevronLeft className="h-7 w-7" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); setImgLightbox((i) => (i === null ? i : (i + 1) % form.imageUrls.length)); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full hover:bg-white/20 text-white z-10" title="Sonraki (→)">
                  <ChevronRight className="h-7 w-7" />
                </button>
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-3 py-1 rounded-full">
                  {imgLightbox + 1} / {form.imageUrls.length}
                </div>
              </>
            )}
            <img src={full} alt="Önizleme" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          </div>
        );
      })()}
    </div>
  );
}

// ======== Main Page (Router) ========

export default function EtsyListingBuilderPage() {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const isNew = searchParams.get("new") === "true";
  const etsyId = searchParams.get("etsyId"); // edit an existing Etsy listing directly

  const [recentDrafts, setRecentDrafts] = useState<DraftListItem[]>([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);

  const [refreshKey, setRefreshKey] = useState(0);
  const refreshDrafts = () => setRefreshKey(k => k + 1);

  const isListMode = !editId && !isNew && !etsyId;

  // Liste aktif magazaya gore filtrelenir; magaza degisince yeniden cekilir
  const activeShop = useActiveShopId();

  useEffect(() => {
    // Taslak listesini SADECE liste modunda cek (builder'i acarken 200 kayit indirme)
    if (!isListMode) return;
    setLoadingDrafts(true);
    fetch(`${API_BASE}/api/listing-drafts?light=1`, { headers: authHeaders() })
      .then((r) => r.json()).then((j) => setRecentDrafts(j.data ?? [])).catch(() => {}).finally(() => setLoadingDrafts(false));
  }, [isListMode, refreshKey, activeShop]);

  if (isListMode) {
    return <DraftListView drafts={recentDrafts} loading={loadingDrafts} onRefresh={refreshDrafts} />;
  }

  return <ListingForm key={etsyId || editId || "new"} draftId={editId} etsyId={etsyId} />;
}


// ======== Taki: kaynak urun + maliyet/kar ========

interface LinkedDesign {
  id: string; name: string; sku: string | null;
  thumbnailUrl: string | null; originalUrl: string; editedUrl: string | null;
  analysisLinks?: Array<{ productTypeKey: string | null; analysis: InspirationAnalysis }>;
  metadata?: { analysisId?: string } | null;
}

/** Ilham alinan analiz: satan Etsy listesi ve fotografi (mockup) */
interface InspirationAnalysis {
  id: string; niche: string | null;
  source: { title: string | null; url: string; imageUrls: string[] } | null;
}

/** Taslak + kaynak tasarimlarin ilham analizleri (tekrarsiz) */
function inspirationsOf(draft: unknown, designs: LinkedDesign[]): InspirationAnalysis[] {
  const direct = (draft as { analysis?: InspirationAnalysis | null } | null)?.analysis;
  const all = [...(direct ? [direct] : []), ...designs.flatMap((d) => (d.analysisLinks ?? []).map((l) => l.analysis))];
  const seen = new Set<string>();
  return all.filter((a) => a && !seen.has(a.id) && (seen.add(a.id), true));
}

interface JewelrySupplierAttr {
  catalogId?: string; categoryId?: string; sku?: string | null;
  cost?: number; shipping?: number; currency?: string; productUrl?: string | null;
}

/** Taki taslaginin kaynagi (tedarikci urunu) ve GUNCEL fiyatla kar hesabi */
interface JewelryPricingAttr {
  /** Hedef net kar % (yeni) ya da eski carpan */
  marginPct?: number; multiplier?: number; commissionPercent?: number;
  /** Taslak fiyati satis x bu katsayi (Etsy'de %50 indirim icin 2); yoksa 1 */
  listPriceFactor?: number;
  entryVariant?: { values: Record<string, string>; multiplier: number } | null;
}

function JewelrySourceCard({ supplier, pricing, comboPrices, price }: {
  supplier: JewelrySupplierAttr;
  pricing: JewelryPricingAttr | null;
  /** En ucuz varyasyon: kombinasyon basina LISTE fiyati */
  comboPrices: Array<{ match: Record<string, string>; price: number }> | null;
  /** Taslak fiyati (liste fiyati) */
  price: number;
}) {
  const { data: products } = useShotProducts(true);
  const item = products?.items.find((p) => p.categoryId === supplier.categoryId && (!supplier.catalogId || p.catalogId === supplier.catalogId));
  const cost = Number(supplier.cost) || 0;
  const shipping = Number(supplier.shipping) || 0;
  // Taslakta liste fiyati durur (satis x2); kar indirimli SATIS fiyatindan,
  // tum Etsy ucretleri (Rexven ile ayni kalemler) dusulerek hesaplanir
  const factor = pricing?.listPriceFactor || 1;
  const sale = price / factor;
  const analysis = etsyProfit(sale, cost, shipping);
  const profit = analysis.net;
  const margin = analysis.marginPct;
  const entryList = comboPrices?.length ? Math.min(...comboPrices.map((c) => c.price)) : null;
  const entrySale = entryList != null ? entryList / factor : null;
  const entryAnalysis = entrySale != null ? etsyProfit(entrySale, cost, shipping) : null;
  const entryProfit = entryAnalysis?.net ?? null;
  const entryLabel = pricing?.entryVariant ? Object.values(pricing.entryVariant.values).filter(Boolean).join(" · ") : "";
  const cur = supplier.currency === "TRY" ? "₺" : "$";
  const coverUrl = item?.coverUrl ? (item.coverUrl.startsWith("http") ? item.coverUrl : `${API_BASE}${item.coverUrl}`) : null;
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-xl border border-fuchsia-200 bg-white p-3 text-xs">
      {coverUrl && <img src={coverUrl} alt="" className="h-14 w-14 rounded-lg object-cover" />}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-fuchsia-600">Kaynak ürün</p>
        <p className="truncate font-medium text-gray-800">{item?.name ?? supplier.sku ?? supplier.categoryId}</p>
        <p className="text-gray-500">
          {supplier.sku ? `${supplier.sku} · ` : ""}{item ? `${item.shotCount} üretilmiş fotoğraf · ` : ""}
          {supplier.catalogId && supplier.categoryId && (
            <Link to={`/listing-builder/jewelry?catalogId=${supplier.catalogId}&categoryId=${supplier.categoryId}`} className="text-fuchsia-700 hover:underline">takı builder'ında aç</Link>
          )}
          {supplier.productUrl && <> · <a href={supplier.productUrl} target="_blank" rel="noreferrer" className="text-fuchsia-700 hover:underline">tedarikçi sayfası</a></>}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-right">
        <span className="text-gray-500">Maliyet</span><span>{cur}{cost.toFixed(2)} + kargo {cur}{shipping.toFixed(2)}</span>
        <span className="text-gray-500">Satış (taban)</span><span className="font-semibold">{cur}{sale.toFixed(2)}{pricing?.marginPct != null ? ` · hedef %${pricing.marginPct}` : pricing?.multiplier ? ` · ×${pricing.multiplier}` : ""}</span>
        {factor !== 1 && (<>
          <span className="text-gray-500">Etsy liste fiyatı (×{factor})</span><span>{cur}{price.toFixed(2)}</span>
        </>)}
        <span className="text-gray-500" title={`İşlem ${cur}${analysis.fees.transaction.toFixed(2)} · Ödeme ${cur}${analysis.fees.payment.toFixed(2)} · Listeleme ${cur}${analysis.fees.listing.toFixed(2)} · Regulatory ${cur}${analysis.fees.regulatory.toFixed(2)}`}>Etsy ücretleri</span>
        <span className="text-gray-600">−{cur}{analysis.fees.total.toFixed(2)}</span>
        <span className="text-gray-500">Net kâr · marj · maliyete</span>
        <span className={profit < 0 ? "font-semibold text-red-600" : "font-semibold text-emerald-700"}>{cur}{profit.toFixed(2)} · %{margin.toFixed(1)} · %{analysis.markupPct.toFixed(0)}</span>
        {entrySale != null && (<>
          <span className="text-amber-700" title={`${comboPrices!.length} kombinasyon bu fiyatla Etsy'ye gider`}>En ucuz varyasyon{entryLabel ? ` (${entryLabel})` : ""}</span>
          <span className="text-amber-800">
            {cur}{entrySale.toFixed(2)} (liste {cur}{entryList!.toFixed(2)}) · net kâr <span className={entryProfit! < 0 ? "font-semibold text-red-600" : ""}>{cur}{entryProfit!.toFixed(2)} (%{entryAnalysis!.marginPct.toFixed(1)})</span>
          </span>
        </>)}
      </div>
    </div>
  );
}
